/**
 * pd-reactive-runtime.v2.js — PageDuo reactive DOM ⇄ model binding runtime (MAJOR v2).
 *
 * v2 = the FREE-`data` model. Unlike v1's fixed namespaces (sources/params/inputs/
 * _status), boot({ data }) makes the model a FREE object the author declares, and
 * directive paths root DIRECTLY at that model (data-pd-text="form.name" reads
 * model.form.name — no namespace wall). Named PageDuo sources are declared via
 * boot({ pdDataSources }): a READ source FILLS data.<name> (the adapted rows array),
 * a WRITE source SENDS FROM data.<name>. Two keys stay RESERVED inside the model:
 * `_status` (per-write save status) and `params` (shared filter axes). Everything
 * else in the model is the author's free data. Transport is unchanged — delegated
 * to PDDataClient exactly as v1. See specs/spec-pd-binding.md.
 *
 * MAJOR-versioned served asset (RUNTIME_FILE in pd-reactive-runtime.registry.ts):
 * published pages bake `/static/pd-reactive-runtime.v1.js` and it is served with a
 * SHORT cache so patches/security fixes within v1 reach every v1 page. Evolve v1
 * ADDITIVELY (new markers/viz/controls never break an old page); cut pd-reactive-
 * runtime.v2.js only for a BREAKING contract change, keeping v1 alive until its
 * deprecation-telemetry traffic hits zero.
 *
 * The one runtime from spec-pd-reactive-runtime.md: a reactive `model` + a CSP-safe
 * declarative `data-pd-*` vocabulary. NO eval / new Function — every attribute
 * value is a NAME or a model PATH.
 *
 * REACTIVITY IS BORROWED, NOT BUILT: this runs on @vue/reactivity (the exact
 * engine Alpine uses) — deep proxy reactivity, batched effects, computed,
 * cleanup. We own only the directive layer + PageDuo data concepts.
 *
 * Alpine-parity implemented here (see spec §10.5):
 *   • reactive engine (borrowed)          • x-model breadth + modifiers  (data-pd-model[.number|.lazy|.debounce|.trim])
 *   • x-text / x-show / x-if              • attribute + class binding    (data-pd-attr / data-pd-class)
 *   • keyed x-for reconciliation          (data-pd-each + data-pd-key — reuse nodes, keep focus)
 *   • event modifiers                     (.debounce/.throttle/.outside/.once on controls + saves)
 *   • lifecycle: MutationObserver (re)hydrate + effect/listener CLEANUP on removal
 *   • x-cloak / x-ignore                  ($dispatch / $watch / $nextTick exposed)
 *   • PageDuo: sources — TWO kinds only: "dataset" (PageDuo; uploads AND submissions
 *     are the same, read via /ds readKey) vs "external" (data from OUTSIDE PageDuo —
 *     proxied today, direct later). Model-declared refresh, Chart.js views, durable
 *     writes (a save appends a row to a dataset).
 */
(function (global) {
  'use strict';
  var VERSION = '2.0.0';
  // ENGINE-MISSING SAFETY: the runtime needs @vue/reactivity. If it isn't loaded
  // (script-order bug, blocked CDN), fail LOUD-but-safe — expose a no-op boot so
  // a consumer never crashes at import, and log once.
  var VR = global.VueReactivity;
  if (!VR || !VR.reactive || !VR.effect) {
    if (global.console) console.error('[pd-reactive-runtime] @vue/reactivity not found — load vue-reactivity.global.js BEFORE this script. Runtime disabled.');
    global.PDReactiveRuntime = { version: VERSION, boot: function (o) { var d = (o && o.data && typeof o.data === 'object') ? o.data : {}; return { model: d, data: d, sources: {}, scan: function () {}, addSource: function () { return Promise.resolve(); }, refetch: function () { return Promise.resolve(); }, removeSource: function () {}, read: function () { return Promise.resolve([]); }, save: function () { return Promise.resolve(); }, status: function () {}, cleanup: function () {}, destroy: function () {}, $watch: function () {}, $dispatch: function () {}, $nextTick: function (f) { f && f(); } }; } };
    return;
  }
  var reactive = VR.reactive, rawEffect = VR.effect, stop = VR.stop, computed = VR.computed, watch = VR.watch;

  // ── batched scheduler: coalesce many model writes in a tick into ONE re-run ──
  var queue = new Set(), scheduled = false;
  function flush() { scheduled = false; var q = Array.from(queue); queue.clear(); q.forEach(function (r) { try { r(); } catch (e) { logErr('effect', e); } }); }
  function enqueue(r) { queue.add(r); if (!scheduled) { scheduled = true; (global.queueMicrotask || function (f) { Promise.resolve().then(f); })(flush); } }

  // PROTOTYPE-POLLUTION GUARD: a path segment can never be __proto__ / constructor
  // / prototype — otherwise `setPath(model,'__proto__.x',…)` would poison
  // Object.prototype for the whole page. Paths are DATA (attribute strings), so we
  // reject these keys rather than traverse/assign into them.
  function unsafeKey(k) { return k === '__proto__' || k === 'constructor' || k === 'prototype'; }
  // PERF: cache the `split('.')` of each path. Paths come from attribute strings —
  // a small, bounded set — so this hot-path allocation happens once per distinct
  // path, not once per get/set. The cached array is READ-ONLY (never mutated).
  var pathCache = Object.create(null), pathCacheN = 0;
  function pathKeys(p) { var k = pathCache[p]; if (!k) { if (pathCacheN >= 2000) { pathCache = Object.create(null); pathCacheN = 0; } k = pathCache[p] = p.split('.'); pathCacheN++; } return k; }
  function getPath(o, p) {
    var ks = pathKeys(p);
    for (var i = 0; i < ks.length; i++) { if (o == null) return undefined; if (unsafeKey(ks[i])) return undefined; o = o[ks[i]]; }
    return o;
  }
  function setPath(o, p, v) {
    var ks = pathKeys(p);
    for (var i = 0; i < ks.length - 1; i++) { if (unsafeKey(ks[i])) return; if (o[ks[i]] == null || typeof o[ks[i]] !== 'object') o[ks[i]] = {}; o = o[ks[i]]; }
    var last = ks[ks.length - 1]; if (unsafeKey(last)) return; o[last] = v;
  }
  // A shared error log — contextual, rate-limited, quiet in prod (only real errors,
  // not per-frame spam). Directive/effect errors are ISOLATED (one bad binding
  // never breaks the rest of the page) then surfaced here.
  var loggedErr = {}, loggedErrN = 0;
  function logErr(ctx, e) { try { var k = ctx + ':' + ((e && e.message) || e); if (loggedErr[k]) return; if (loggedErrN >= 500) { loggedErr = {}; loggedErrN = 0; } loggedErr[k] = 1; loggedErrN++; if (global.console) console.error('[pd-reactive-runtime] ' + ctx + ' error:', (e && e.message) || e); } catch (_) {} }

  // i18n: number/date formatting honors the PAGE's language (document lang), not
  // the viewer's browser locale — a page authored in `de` shows German grouping
  // to everyone. Dates (Date instances) format locale-aware; strings pass through
  // unchanged (a date INPUT gives an ISO string, shown as-is). Unicode/RTL/CJK/
  // emoji need no special handling — JS strings + textContent are UTF-16 native.
  function pageLang() { try { var l = global.document && document.documentElement && document.documentElement.lang; return l || undefined; } catch (e) { return undefined; } }
  function fmt(v) {
    // DISPLAY a value as-is — like Vue/Alpine, the runtime does NOT auto-format
    // numbers: grouping would corrupt IDs / years / versions (2026 → "2,026") and
    // rounding would silently lose data. Numbers render RAW; NaN/Infinity → empty.
    // Grouping/currency/relative-time is the author's job (compute it into the
    // model). Date INSTANCES are the one exception — locale-formatted to the PAGE's
    // lang (an ISO date STRING passes through unchanged). Unicode/RTL/CJK/emoji
    // need nothing special — JS strings + textContent are UTF-16 native.
    if (typeof v === 'number') return isFinite(v) ? String(v) : '';
    if (v instanceof Date) { try { return isNaN(v.getTime()) ? '' : v.toLocaleString(pageLang()); } catch (e) { return ''; } }
    return v == null ? '' : String(v);
  }
  // KPI-only display: a `data-pd-viz="number"` card binds straight to a fetched
  // aggregate, so the author can't format it (unlike data-pd-text, where raw is
  // correct). Show it the way every BI tool does — locale-aware COMPACT notation
  // ("6.66T", "1.2K"). This is a headline summary, not the underlying data, so
  // compacting is intended here (and only here); everything else stays raw via fmt().
  function fmtMetric(v) {
    var n = Number(v);
    if (v == null || v === '' || !isFinite(n)) return fmt(v);
    try { return new Intl.NumberFormat(pageLang(), { notation: 'compact', maximumFractionDigits: 2 }).format(n); } catch (e) { return fmt(n); }
  }
  // Optional DISPLAY formatting for data-pd-text (companion attr data-pd-format="<kind>[:arg]").
  // Turns a raw STORED value into a locale-aware human string via Intl — the MODEL keeps the
  // raw value; only what the user SEES changes. Kinds: date/time/datetime (a timestamp or ISO
  // string → "Jul 21, 2026" / "9:00 AM" / both), number/integer, percent (0.42 → "42%"),
  // currency (arg = ISO code, default USD → "currency:EUR"). Anything unknown or unparseable
  // falls back to the raw fmt() — it NEVER throws, so a bad spec can't blank the page.
  var FMT_DATE = { date: { dateStyle: 'medium' }, time: { timeStyle: 'short' }, datetime: { dateStyle: 'medium', timeStyle: 'short' } };
  function formatValue(raw, spec) {
    if (raw == null || raw === '') return '';
    var i = String(spec).indexOf(':'); var kind = i < 0 ? String(spec) : String(spec).slice(0, i); var arg = i < 0 ? '' : String(spec).slice(i + 1);
    try {
      if (FMT_DATE[kind]) {
        var d = (raw instanceof Date) ? raw : new Date(raw);
        if (isNaN(d.getTime())) return fmt(raw);
        return new Intl.DateTimeFormat(pageLang(), FMT_DATE[kind]).format(d);
      }
      var n = Number(raw); if (!isFinite(n)) return fmt(raw);
      if (kind === 'integer') return new Intl.NumberFormat(pageLang(), { maximumFractionDigits: 0 }).format(n);
      if (kind === 'number') return new Intl.NumberFormat(pageLang()).format(n);
      if (kind === 'percent') return new Intl.NumberFormat(pageLang(), { style: 'percent', maximumFractionDigits: 2 }).format(n);
      if (kind === 'currency') return new Intl.NumberFormat(pageLang(), { style: 'currency', currency: (arg || 'USD') }).format(n);
    } catch (e) { /* fall through to raw */ }
    return fmt(raw);
  }
  // ── chart config (Subsystem D) — every supported data-pd-viz, single- OR multi-
  //    measure. Row shapes the runtime accepts from /ds:
  //      • { label, value }                 → single series (bar/hbar/line/area/pie/…)
  //      • { label, values:{s1:n,s2:n} }    → multi series (stacked-bar/-area, grouped)
  //      • { x, y [, r] }                   → point data (scatter / bubble)
  var CHART_PALETTE = ['#14b8a6', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#ec4899', '#0ea5e9', '#f97316', '#10b981', '#a855f7'];
  function numOr0(x) { var n = Number(x); return isFinite(n) ? n : 0; }
  function seriesKeysOf(rows) { for (var i = 0; i < rows.length; i++) { var vo = rows[i] && (rows[i].values || rows[i].series); if (vo && typeof vo === 'object') return Object.keys(vo); } return null; }
  // Adapt a raw /ds response into the render shape. The dataset backend returns rows
  // keyed by COLUMN NAME ({ "Region":"Asia", "sum_Total_Revenue":486e9 }) plus
  // groupBy/aggregations metadata; the render layer (chartConfigFor / number / table)
  // consumes { label, value } (single) or { label, values:{series} } (multi). This is
  // the same server→render mapping the legacy card-hydration renderer does client-side.
  // Rows already in render shape (mocks, pre-shaped responses) pass through untouched.
  function adaptServerRows(j) {
    var rows = (j && (j.rows || j.data)) || [];
    if (!rows.length) return rows;
    var s0 = rows[0];
    if (!s0 || typeof s0 !== 'object') return rows;
    if ('label' in s0 || 'value' in s0 || 'values' in s0 || 'series' in s0 || 'x' in s0 || 'y' in s0) return rows; // already render-shaped
    var gb = (j && j.groupBy) || [];
    var gbSet = {}; for (var g = 0; g < gb.length; g++) gbSet[gb[g]] = 1;
    var measures = Object.keys(s0).filter(function (k) { return !gbSet[k]; });
    var m0 = measures[0];
    if (m0 == null) return rows; // no measure column → nothing to plot
    if (gb.length >= 2) { // multi-dimensional → pivot dims 2..N into series keyed by their joined values
      var byLabel = {}, order = [];
      rows.forEach(function (r) {
        var lab = r[gb[0]], sk = [];
        for (var i = 1; i < gb.length; i++) sk.push(r[gb[i]]);
        if (!byLabel[lab]) { byLabel[lab] = { label: lab, values: {} }; order.push(lab); }
        byLabel[lab].values[sk.join(' · ')] = numOr0(r[m0]);
      });
      return order.map(function (l) { return byLabel[l]; });
    }
    var labelCol = gb.length ? gb[0] : null;
    return rows.map(function (r) {
      // Preserve the RAW server columns (e.g. `sum_Total_Revenue`, `count`) on the
      // adapted row so a page's OWN inline script can read a KPI by its real column
      // name; the render-shape keys (label/value/x/y) are layered ON TOP for the
      // chart/number vizzes. Before this, adapting an aggregate row to {label,value}
      // DROPPED the raw column, so `+row.sum_Total_Revenue` was NaN — the owner's
      // "$NaN" KPIs on a Sales & Revenue dashboard (2026-07-14). Charts ignore the
      // extra keys; the render-shape keys win any name collision.
      var out = {};
      for (var k in r) { if (Object.prototype.hasOwnProperty.call(r, k)) out[k] = r[k]; }
      out.label = labelCol != null ? r[labelCol] : undefined;
      out.value = numOr0(r[m0]);
      if (measures.length > 1) { out.values = {}; measures.forEach(function (mc) { out.values[mc] = numOr0(r[mc]); }); } // multi-measure → grouped series
      out.x = labelCol != null ? r[labelCol] : numOr0(r[m0]);                     // scatter/bubble fallbacks
      out.y = numOr0(r[measures.length > 1 ? measures[1] : m0]);
      return out;
    });
  }
  // Filter axes are described by the backend as `params:[{name,domain}]`; the bind
  // layer reads `domains:{ axis:[...] }`. Merge both (an explicit domains object wins).
  function mergeDomains(j) {
    var out = (j && j.domains) || {};
    if (j && Array.isArray(j.params)) j.params.forEach(function (p) { if (p && p.name && Array.isArray(p.domain) && !out[p.name]) out[p.name] = p.domain; });
    return out;
  }
  // A date/number/range filter has BOUNDS, not a value list: the backend sends
  // `params:[{name,range:{min,max}}]` so a slider spans the real data and a date picker
  // cannot be dragged outside it. Kept beside the domains because a param has one or the
  // other, never both — a control with a `range` never reaches the domain branches.
  function mergeRanges(j) {
    var out = {};
    if (j && Array.isArray(j.params)) j.params.forEach(function (p) {
      if (p && p.name && p.range && p.range.min != null && p.range.max != null) out[p.name] = p.range;
    });
    return out;
  }
  // A filter's DECLARED default, as the server applies it when the page sends no value. A
  // generated control must SHOW that state, or it names one thing while the data is another.
  // Blank (absent / null / "") means "unfiltered", the same reading the server gives it.
  function mergeDefaults(j) {
    var out = {};
    if (j && Array.isArray(j.params)) j.params.forEach(function (p) {
      if (p && p.name && p.kind === 'filter') out[p.name] = (p['default'] == null || String(p['default']).trim() === '') ? '' : String(p['default']);
    });
    return out;
  }
  // The label for "no filter" in a generated control, in the PAGE's language (the same
  // `document lang` the number formats follow). Unknown languages fall back to English.
  var ALL_LABELS = { en: 'All', vi: 'Tất cả', es: 'Todos', fr: 'Tous', de: 'Alle', pt: 'Todos', it: 'Tutti', nl: 'Alle',
    ru: 'Все', uk: 'Усі', pl: 'Wszystkie', tr: 'Tümü', ar: 'الكل', ur: 'تمام', hi: 'सभी', mr: 'सर्व', bn: 'সব', ta: 'அனைத்தும்',
    te: 'అన్నీ', sw: 'Zote', id: 'Semua', ms: 'Semua', th: 'ทั้งหมด', ja: 'すべて', ko: '전체', zh: '全部', yue: '全部' };
  function allLabel() { var l = (pageLang() || 'en').toLowerCase().split(/[-_]/)[0]; return ALL_LABELS[l] || ALL_LABELS.en; }
  // Pure: rows + viz → a Chart.js config. No DOM, no globals beyond the palette.
  // A measure column's name as a reader says it: `sum_revenue` → "Revenue",
  // `avg_unit_price` → "Unit price (avg)". Column names come from the data, so only the
  // aggregation prefix is ours to translate.
  function measureTitle(k) {
    var m = /^(sum|avg|min|max|count_unique|count)(?:_(.+))?$/.exec(String(k));
    var base = m ? (m[2] || m[1]) : String(k);
    var nice = base.replace(/_/g, ' '); nice = nice.charAt(0).toUpperCase() + nice.slice(1);
    return !m || m[1] === 'sum' || !m[2] ? nice : nice + ' (' + (m[1] === 'count_unique' ? 'unique' : m[1]) + ')';
  }
  // Axis labels that are date buckets read as dates: a month bucket arrives as
  // "2026-01-01", and six of those along an axis read as noise. First-of-month dates
  // print as "Jan 2026", other dates as a short date, in the PAGE's language.
  var ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00:00(?:\.0+)?Z?)?$/;
  function dateAxisLabels(labels) {
    if (!labels.length || !labels.every(function (l) { return typeof l === 'string' && ISO_DAY.test(l); })) return labels;
    var months = labels.every(function (l) { return ISO_DAY.exec(l)[3] === '01'; });
    try {
      var f = new Intl.DateTimeFormat(pageLang(), months ? { month: 'short', year: 'numeric', timeZone: 'UTC' } : { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
      return labels.map(function (l) { var p = ISO_DAY.exec(l); return f.format(new Date(Date.UTC(+p[1], +p[2] - 1, +p[3]))); });
    } catch (e) { return labels; }
  }
  function chartConfigFor(rows, viz, label) {
    rows = rows || [];
    var labels = dateAxisLabels(rows.map(function (r) { return r != null ? (r.label != null ? r.label : r.x) : undefined; }));
    // SEPARATE MEASURES NEVER STACK. Stacking adds the series on one axis, which is right for
    // parts of one whole (revenue split by region) and wrong for two measures: a generated
    // "revenue and units by product" stacked-bar drew dollars plus units as one bar, so
    // Croissant read ~25,000 (2026-09-29). A multi-measure read keeps its raw columns on each
    // row (adaptServerRows), a pivot does not, so the rows say which one this is. Two
    // measures get two scales (the combo chart); more than two sit side by side.
    var mk0 = seriesKeysOf(rows);
    var measureSeries = !!(mk0 && mk0.length > 1 && rows[0] && mk0.every(function (k) { return Object.prototype.hasOwnProperty.call(rows[0], k); }));
    if (measureSeries && (viz === 'stacked-bar' || viz === 'stacked-area')) viz = mk0.length === 2 ? 'combo' : (viz === 'stacked-area' ? 'area' : 'bar');
    var stacked = viz === 'stacked-bar' || viz === 'stacked-area';
    var isArea = viz === 'area' || viz === 'stacked-area';
    var legend = false, type, data;
    if (viz === 'scatter' || viz === 'bubble') {
      var pts = rows.map(function (r) { var p = { x: numOr0(r.x != null ? r.x : r.label), y: numOr0(r.y != null ? r.y : r.value) }; if (viz === 'bubble') p.r = numOr0(r.r != null ? r.r : (r.size != null ? r.size : 6)); return p; });
      type = viz; data = { datasets: [{ label: label || 'Value', data: pts, backgroundColor: CHART_PALETTE[0] }] };
    } else {
      var mkeys = seriesKeysOf(rows);
      if (mkeys && mkeys.length) { // MULTI-series
        legend = true;
        var isCombo = viz === 'combo';
        data = { labels: labels, datasets: mkeys.map(function (k, ix) {
          var ds = { label: measureSeries ? measureTitle(k) : k, data: rows.map(function (r) { return numOr0((r.values || r.series || {})[k]); }), backgroundColor: CHART_PALETTE[ix % CHART_PALETTE.length], borderColor: CHART_PALETTE[ix % CHART_PALETTE.length], fill: isArea };
          // COMBO: 1st measure = bars on the LEFT axis, 2nd+ = a line on the RIGHT axis (dual-axis).
          if (isCombo) { ds.type = ix === 0 ? 'bar' : 'line'; ds.yAxisID = ix === 0 ? 'y' : 'y1'; if (ix > 0) { ds.fill = false; ds.tension = 0.35; ds.borderWidth = 2; ds.pointRadius = 2; } }
          return ds;
        }) };
        type = isArea ? 'line' : 'bar'; // combo base type is 'bar' too
      } else { // SINGLE series
        var values = rows.map(function (r) { return r ? r.value : undefined; });
        var categorical = viz === 'pie' || viz === 'doughnut' || viz === 'polarArea';
        legend = categorical;
        var bg = categorical ? labels.map(function (_, ix) { return CHART_PALETTE[ix % CHART_PALETTE.length]; }) : CHART_PALETTE[0];
        // `combo` is a MULTI-series viz (bars + a line on a second axis). When the rows come
        // back single-series it has no second measure to draw, but it must still degrade to a
        // plain bar chart: passing 'combo' to Chart.js as a type throws "not a registered
        // controller", which kills the binding and takes the whole widget with it — and every
        // OTHER multi-only viz was already mapped here, so this was the one that leaked.
        type = (viz === 'hbar' || viz === 'stacked-bar' || viz === 'combo') ? 'bar' : isArea ? 'line' : viz;
        data = { labels: labels, datasets: [{ label: label || 'Value', data: values, backgroundColor: bg, borderColor: categorical ? '#ffffff' : '#0d9080', fill: isArea }] };
      }
    }
    var options = { indexAxis: viz === 'hbar' ? 'y' : 'x', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: legend } } };
    if (stacked) options.scales = { x: { stacked: true }, y: { stacked: true } };
    if (viz === 'combo') options.scales = { y: { position: 'left', beginAtZero: true }, y1: { position: 'right', beginAtZero: true, grid: { drawOnChartArea: false } } };
    // WHOLE THINGS STEP IN WHOLE NUMBERS. A tally of 1 and 2 votes drew ticks at 0.2, 0.4 …, and
    // "1.4 votes" is not a quantity (2026-09-29). When every value an axis carries is an integer,
    // its ticks are too. Per axis, so a combo's revenue scale keeps its cents.
    if (type === 'bar' || type === 'line') {
      var valueAxis = viz === 'hbar' ? 'x' : 'y';
      var intsOn = function (axisId) {
        var sets = data.datasets.filter(function (d) { return (d.yAxisID || 'y') === axisId; });
        return sets.length && sets.every(function (d) { return d.data.every(function (v) { return v == null || Math.floor(v) === v; }); });
      };
      [valueAxis, 'y1'].forEach(function (ax) {
        if (ax === 'y1' && viz !== 'combo') return;
        if (!intsOn(ax === 'x' ? 'y' : ax)) return;
        options.scales = options.scales || {};
        var sc = options.scales[ax] || {};
        options.scales[ax] = Object.assign({}, sc, { ticks: Object.assign({}, sc.ticks || {}, { precision: 0 }) });
      });
    }
    return { type: type, data: data, options: options };
  }

  // URL-bearing attributes — sanitized (no javascript:/vbscript:) when bound via data-pd-attr.
  var URL_ATTRS = { href: 1, src: 1, action: 1, formaction: 1, poster: 1, background: 1, cite: 1, 'xlink:href': 1, ping: 1, srcset: 1, 'xmlns:xlink': 0 };
  // Cross-browser helpers — avoid the few DOM APIs with a floor above Proxy's
  // (NodeList.forEach: Safari 10; Element.closest: Safari 9; classList.toggle's
  // 2nd arg: old Safari quirk). Works on ANY browser incl. old mobile WebViews.
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function ignored(el) { for (var n = el; n; n = n.parentNode) { if (n.nodeType === 1 && n.hasAttribute && n.hasAttribute('data-pd-ignore')) return true; } return false; }
  function toggleClass(el, cls, on) { if (on) el.classList.add(cls); else el.classList.remove(cls); }
  function paramsQuery(params) { var q = []; for (var k in params) { var v = params[k]; if (v === '' || v == null) continue; (Array.isArray(v) ? v : [v]).forEach(function (x) { q.push('p.' + encodeURIComponent(k) + '=' + encodeURIComponent(x)); }); } return q.join('&'); }

  // data-pd-set="path:value" — the value is a CLOSED literal, never an expression:
  // keywords (true/false/null), a finite number, or a (optionally quoted) string.
  function coerceLiteral(raw) {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    if (raw === 'null') return null;
    if (raw === '') return '';
    var c0 = raw.charAt(0), cN = raw.charAt(raw.length - 1);
    if (raw.length >= 2 && ((c0 === '"' && cN === '"') || (c0 === "'" && cN === "'"))) return raw.slice(1, -1); // quoted → literal string
    if (/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(raw)) { var n = Number(raw); if (isFinite(n)) return n; }
    return raw;
  }
  // SNAPSHOT a value out of the reactive proxy before it goes on the wire — a save
  // payload must be plain, detached data (never a live proxy that mutates mid-flight).
  function snapshot(v) {
    if (v == null || typeof v !== 'object') return v;
    try { return JSON.parse(JSON.stringify(v)); } catch (e) {}
    try { return global.structuredClone ? global.structuredClone(v) : v; } catch (e2) { return v; }
  }

  // Known data-pd-* attribute names — anything else is a typo/unhandled marker.
  // The CLOSED directive vocabulary. Every entry below is CONSUMED by a directive
  // handler in scan() (or read as a sub-attr of one) — kept in lock-step with the
  // handlers by pd-reactive-runtime.coverage.matrix.js's parity case. (2026-07-12:
  // dropped the vestigial `data-pd-param` — the real one is `data-pd-params` — and
  // `data-pd-write`: the write DECLARATION is merged onto the `data-pd-save` element
  // via data-pd-write-target/-fields, so a separate `data-pd-write` is never read.)
  var KNOWN_ATTRS = ['data-pd-text', 'data-pd-format', 'data-pd-show', 'data-pd-if', 'data-pd-attr', 'data-pd-class', 'data-pd-model', 'data-pd-model-mods', 'data-pd-each', 'data-pd-key', 'data-pd-field', 'data-pd-remove', 'data-pd-bind', 'data-pd-bind-mods', 'data-pd-control', 'data-pd-from', 'data-pd-fields', 'data-pd-dataset', 'data-pd-viz', 'data-pd-label', 'data-pd-save', 'data-pd-save-mods', 'data-pd-write-target', 'data-pd-write-fields', 'data-pd-query', 'data-pd-query-mods', 'data-pd-set', 'data-pd-toggle', 'data-pd-cloak', 'data-pd-ignore'];

  // One-time DEFAULT styling for runtime-rendered widgets. `:where()` gives it ZERO
  // specificity — a pure default that ANY author rule (class/id/element) overrides. Fixes the
  // KPI case: a `data-pd-viz="number"` figure renders the aggregate as its own text, so without
  // this it inherits the tiny default font while a hand-coded number card looks big — an
  // inconsistent dashboard. Now a live KPI reads as a KPI out of the box; author CSS still wins.
  var stylesInjected = false;
  function injectRuntimeStyles(doc) {
    if (stylesInjected || !doc) return;
    try {
      if (doc.getElementById('pd-rt-css')) { stylesInjected = true; return; }
      var css = ':where(figure[data-pd-viz="number"]){display:block;margin:0;font-size:2rem;line-height:1.1;font-weight:800;font-variant-numeric:tabular-nums}';
      var s = doc.createElement('style'); s.id = 'pd-rt-css'; s.textContent = css;
      (doc.head || doc.documentElement).appendChild(s); stylesInjected = true;
    } catch (e) {}
  }

  // The external-data PROXY endpoint. "external" = data from OUTSIDE PageDuo, addressed
  // by a URL: fetched through the PageDuo proxy (server-side keys/CORS/SSRF cover) or
  // DIRECT (browser→URL). This is only the FALLBACK when PDDataClient isn't loaded — the
  // client owns proxy/direct normally. The server serves the proxy at /live-data/proxy
  // today; renaming that route to /external is a LATER server-side step.
  var EXTERNAL_PROXY = '/api/v1/live-data/proxy?url=';

  /**
   * The app booted on THIS page, if any. A page normally boots once — but generated pages do
   * duplicate their boot snippet (a per-slide deck concatenates one copy per slide), and a
   * re-mounting host can call boot twice. Re-booting would build a SECOND reactive graph, a
   * SECOND MutationObserver and a SECOND set of source fetches over the same DOM, so every
   * marker would end up driven by two models that immediately disagree.
   *
   * Owner direction 2026-08-17: absorb the mistake HERE rather than throwing it back at the
   * model — a validator round-trip costs a full generation call, and the runtime can make the
   * duplicate harmless for free.
   */
  var BOOTED = null;

  function boot(opts) {
    opts = opts || {};

    // ── ALREADY BOOTED → merge and return the SAME app ────────────────────────────────────
    // Not a no-op: a second boot may legitimately carry aliases or sources the first did not,
    // so anything NEW is folded in. Anything already present is left ALONE — the first boot
    // owns it, and overwriting would discard whatever the user has typed into it since.
    if (BOOTED) {
      try {
        var _d = (opts.data && typeof opts.data === 'object') ? opts.data : null;
        if (_d) for (var _k in _d) {
          if (!Object.prototype.hasOwnProperty.call(_d, _k)) continue;
          if (_k === '_status' || _k === 'params') continue;
          if (BOOTED.model[_k] === undefined) BOOTED.model[_k] = _d[_k];
        }
        var _s = opts.pdDataSources || opts.sources;
        if (_s) for (var _n in _s) {
          if (!Object.prototype.hasOwnProperty.call(_s, _n)) continue;
          if (!BOOTED.sources[_n]) { try { BOOTED.addSource(_n, _s[_n]); } catch (e) {} }
        }
        // New markup may have arrived with the duplicate block; wire whatever is unbound.
        try { BOOTED.scan(); } catch (e) {}
      } catch (e) {}
      return BOOTED;
    }
    var apiFetch = opts.fetch || function (u, i) { return fetch(u, i); };
    // TEMP DEBUG (owner incident 2026-07-25) — enable with `localStorage.pd_debug='1'` then reload.
    // Traces boot data → draftLoad → save so we can see WHERE a capture model loses its fields.
    var DBG = (function () { try { return !!(global.__PD_DEBUG || (global.localStorage && global.localStorage.getItem('pd_debug'))); } catch (e) { return false; } })();
    function dlog() { if (DBG && global.console) { try { console.log.apply(console, ['[pd-debug]'].concat([].slice.call(arguments))); } catch (e) {} } }
    // v2: the model is a FREE object the author declares (boot({ data })). Directive
    // paths root DIRECTLY at it. Two keys are RESERVED and owned by the runtime:
    // `_status` (per-write save status) + `params` (shared filter axes). We seed them
    // as reactive objects so setStatus / data-pd-bind have a live target (respecting
    // an author who deliberately pre-populated either key).
    var model = reactive(opts.data && typeof opts.data === 'object' ? opts.data : {});
    if (model._status == null || typeof model._status !== 'object') model._status = {};
    if (model.params == null || typeof model.params !== 'object') model.params = {};
    if (DBG) { try { global.__pdBootN = (global.__pdBootN || 0) + 1; } catch (e) {} var _bd = []; for (var _a in (opts.data || {})) { if (_a === '_status' || _a === 'params') continue; try { var _s = opts.data[_a]; _bd.push(_a + ' = ' + ((_s && typeof _s === 'object') ? (Object.keys(_s).length + ' keys, assessmentId:' + (('assessmentId' in _s) ? _s.assessmentId : 'MISSING!')) : typeof _s)); } catch (e) {} } dlog('===== BOOT #' + (global.__pdBootN || '?') + ' =====', _bd.length ? _bd.join('  |  ') : '(EMPTY — boot() got NO data aliases!)'); dlog('  boot() called from:', (new Error().stack || '(no stack)').split('\n').slice(2, 6).map(function (l) { return l.trim(); }).join('  <-  ')); }
    var sources = {}; // internal registry: name -> { kind, readKey, writeKey, url, transport, params, refresh, domains, ... }
    // ── multi-page DRAFT persistence ────────────────────────────────────────
    // A form can span several linked .html pages (plain <a href>, no nav JS). To
    // carry the visitor's entries across that navigation we mirror the FREE model —
    // everything EXCEPT the reserved keys (`_status`, `params`) and the source-result
    // keys (a read's data.<name> is server-owned, never a draft) — to localStorage
    // under a stable, author-declared key and REHYDRATE it on the next page's boot —
    // so step 2 sees what was typed on step 1. Give EVERY page of the form the SAME
    // boot({ draftKey }). A successful save() clears the draft. No draftKey ⇒ no
    // persistence (default).
    var draftKey = opts.draftKey != null ? String(opts.draftKey) : null;
    var DRAFT_PREFIX = 'pd_draft_v1:';
    var DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — an ABANDONED draft self-expires so localStorage never accretes stale forms
    var DRAFT_MAX = 1000;       // hard backstop on stored drafts per origin — beyond it, LRU-evict the oldest (TTL is the primary reclaim)
    var DRAFT_MAX_BYTES = 1e6;  // never persist an absurdly large single draft (paste-bomb quota guard)
    var rehydratedPaths = {}; // top-level model key restored from a draft — skip the DOM re-seed below
    var draftRunner = null;
    var draftTimer = null; // pending debounced-persist timer — cancelled by clearDraft (so a clear STICKS) and by pagehide (flush now)
    var disposers = new WeakMap(); // el -> [fn]
    // Dev-mode warnings — help debug typos/malformed bindings; SILENT in prod.
    // On by default on localhost, or via opts.dev / window.__PD_DEV__.
    var DEV = opts.dev === true || global.__PD_DEV__ === true ||
      (opts.dev !== false && global.location && /^(localhost$|127\.|\[?::1)/.test(String(location.hostname || '')));
    var warned = {}, warnedN = 0;
    function warn(m) { if (DEV && global.console && !warned[m]) { if (warnedN >= 500) { warned = {}; warnedN = 0; } warned[m] = 1; warnedN++; console.warn('[pd-reactive-runtime] ' + m); } }
    // RENDER cap — bounds ONLY how many DOM nodes the keyed list (data-pd-each) and
    // the HTML table view materialize. It NEVER touches the data model: the model
    // holds every row, and submission + mutations (data-pd-save, data-pd-remove)
    // read the FULL list — only the *view* is bounded, nothing is dropped from the
    // data. It's a CATASTROPHE CEILING, not a UX limit: its sole job is to stop a
    // huge list from making hundreds-of-thousands / millions of nodes and freezing/
    // OOMing the tab. Set high enough that no reasonable page hits it (10000 » any
    // hand-authored table) yet far below the freeze zone. Legit large data is
    // aggregated/paginated SERVER-side (the /ds query returns bounded rows) or
    // virtualized — the cap is a backstop, never the mechanism. Tunable via
    // opts.maxRows (exposed on the boot return as `maxRows`).
    var MAX_ROWS = opts.maxRows > 0 ? opts.maxRows : 10000;
    // RESERVED editor namespace. The clawDrive EDITOR injects its own transient
    // chips — `data-pd-edit-noun` (the "Edit" label), `data-pd-use-hint` (the "Use"
    // pill) — into the SAME preview the runtime boots in. Those are editor UI, NOT
    // runtime directives and NOT typos, so the runtime must not flag them as
    // "unknown marker" — otherwise the editor console fills with false warnings that
    // read as page defects. They never persist to saved/published HTML (overlay-only),
    // so the server-side validateVocabulary intentionally does NOT skip them (a leaked
    // chip there IS a bug worth flagging); this skip is live-preview-only.
    function isEditorAttr(n) { return n.indexOf('data-pd-edit') === 0 || n.indexOf('data-pd-use') === 0; }
    function checkUnknown(root) {
      if (!DEV) return;
      qsa(root, '*').forEach(function (el) {
        var at = el.attributes; if (!at) return;
        for (var i = 0; i < at.length; i++) {
          var n = at[i].name;
          if (n.indexOf('data-pd-') === 0 && !isEditorAttr(n) && KNOWN_ATTRS.indexOf(n) < 0 && !dottedParts(n)) warn('unknown marker "' + n + '" on <' + el.tagName.toLowerCase() + '> — typo? (no directive handles it)');
        }
      });
    }

    function addDisposer(el, fn) { var a = disposers.get(el); if (!a) { a = []; disposers.set(el, a); } a.push(fn); }
    // A reactive effect wired to the batched scheduler + registered for cleanup.
    // The fn is wrapped so a throw (bad path, throwing renderer) is ISOLATED +
    // logged — one broken binding never breaks the reactive graph or the page.
    function bind(el, fn) {
      var safe = function () { try { fn(); } catch (e) { logErr('binding', e); } };
      var runner = rawEffect(safe, { scheduler: function () { enqueue(runner); } });
      addDisposer(el, function () { stop(runner); });
      return runner;
    }
    function listen(el, target, ev, fn, capture) { target.addEventListener(ev, fn, capture); addDisposer(el, function () { target.removeEventListener(ev, fn, capture); }); }

    // ── event-modifier wrappers (ported from Alpine) ──
    function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }
    function throttle(fn, ms) { var last = 0; return function () { var n = Date.now(); if (n - last >= ms) { last = n; fn.apply(this, arguments); } }; }
    function applyMods(el, fn, mods) {
      if (mods.indexOf('debounce') >= 0) fn = debounce(fn, 300);
      if (mods.indexOf('throttle') >= 0) fn = throttle(fn, 300);
      if (mods.indexOf('once') >= 0) { var done = false; var g = fn; fn = function () { if (done) return; done = true; g.apply(this, arguments); }; }
      return fn;
    }
    function modsOf(el, attr) { return (el.getAttribute(attr + '-mods') || '').split('.').filter(Boolean); }
    // data-pd-model's modifiers (the registry's model mods) — also recognized at the END of a path.
    var MODEL_MOD_NAMES = ['number', 'lazy', 'trim', 'boolean', 'debounce'];
    // Can `mod` be a modifier on the value `v` that `el` edits? Only when it FITS what is there:
    // `.number` on a number (or on a null / '' declared for a number or range input), `.boolean` on a
    // boolean (or a null checkbox), `.trim` / `.lazy` / `.debounce` on any plain value. So a text
    // `phone: ''` bound as `phone.number` is never turned into a number (and "0912 345" into null),
    // and an UNDECLARED parent is never guessed at: `phone.number` there is a field as often as not.
    function modFitsValue(mod, v, el) {
      if (v === undefined || (v !== null && typeof v === 'object')) return false;
      var t = (el.type || '').toLowerCase();
      if (mod === 'number') return typeof v === 'number' || ((v === null || v === '') && (t === 'number' || t === 'range'));
      if (mod === 'boolean') return typeof v === 'boolean' || (v === null && t === 'checkbox');
      return true;
    }

    // ── DATA transport: fetch + durable write are DELEGATED to PDDataClient (resilient
    //    — retry, timeout, coalesce identical reads, durable idempotent write queue).
    //    The runtime owns WHEN (refresh cadence, param-driven refetch, visibility); the
    //    client owns HOW. Graceful fallback to a bare fetch so the runtime still works
    //    standalone if the client isn't loaded. ──
    var dc = opts.dataClient ||
      (global.PDDataClient && global.PDDataClient.boot
        ? global.PDDataClient.boot({ fetch: opts.fetch, mode: opts.mode, baseUrl: opts.baseUrl, auth: opts.auth })
        : null);
    // sources: alias → { kind, readKey|url, transport, params, refresh, __seq, __timer }.
    // A dataset's IDENTITY is its readKey (a query capability that may span datasets +
    // survives renames); the alias is a page-local handle. External is JUST A URL,
    // fetched via the proxy (default — server handles CORS/keys/SSRF) or transport:
    // "direct" (browser→URL, https-only). Sources are declared via boot({ sources }) /
    // addSource() — JS config, NEVER a deletable DOM element.
    // v2: a source carries WHATEVER capabilities the server issued — readKey, writeKey,
    // or BOTH — plus (after a read) its `domains` for filter controls. There is NO
    // read-only/write-only TYPE; a source just HAS keys. `url` ⇒ external (proxied/direct).
    function normalizeSource(desc) {
      desc = desc || {};
      var kind = desc.url ? 'external' : 'dataset';
      // `draft: true` opts THIS source's data.<alias> into cross-page localStorage persistence,
      // keyed by the alias (the source's stable identity). The per-source flag replaces the
      // top-level draftKey: a write source that should carry across pages is `{ writeKey, draft: true }`.
      return { kind: kind, readKey: desc.readKey || '', writeKey: desc.writeKey || '', url: desc.url || '', transport: desc.transport || '', params: desc.params || null, refresh: parseInt(desc.refresh || 0, 10) || 0, domains: null, draft: !!desc.draft };
    }
    function readDescriptor(s) {
      if (s.kind === 'external') return { kind: 'external', url: s.url, transport: s.transport, params: s.params };
      return { kind: 'dataset', readKey: s.readKey, params: model.params }; // dataset params = the shared filter state
    }
    function fallbackRead(desc) { // used only when no PDDataClient is present
      if (desc.kind === 'external') {
        var eurl = desc.params ? (desc.url + (desc.url.indexOf('?') < 0 ? '?' : '&') + new URLSearchParams(desc.params)) : desc.url;
        return apiFetch(desc.transport === 'direct' ? eurl : EXTERNAL_PROXY + encodeURIComponent(eurl)).then(function (r) { return r.json(); });
      }
      var qs = paramsQuery(desc.params || {}); return apiFetch('/api/v1/ds/' + encodeURIComponent(desc.readKey) + (qs ? '?' + qs : '')).then(function (r) { return r.json(); });
    }
    // FETCH a source → resolve the fresh data AND immutable-REPLACE model[alias] with the
    // ADAPTED ROWS ARRAY (v2: a read FILLS data.<name>). A whole-array swap is the cleanest
    // reactive "changed" signal; a stale response from a superseded fetch is dropped by the
    // seq guard. The read's `domains` are stashed on the REGISTRY entry (off the model) for
    // filter controls. A write-only source (no readKey/url) has nothing to read → no-op.
    function fetchSource(alias) {
      var s = sources[alias]; if (!s) return Promise.resolve();
      if (!s.readKey && !s.url) return Promise.resolve(); // write-only — nothing to read
      var seq = (s.__seq = (s.__seq || 0) + 1);
      var desc = readDescriptor(s);
      var read = dc ? dc.read(desc) : fallbackRead(desc);
      return read.then(function (j) {
        if (s.__seq !== seq) return model[alias]; // superseded — keep the newest
        if (s.kind === 'external') {
          model[alias] = (j && j.data != null) ? j.data : j;        // external → data.<name> = the value
        } else {
          s.domains = mergeDomains(j);                              // filter domains live on the registry now
          s.ranges = mergeRanges(j);                                // …and their numeric/date bounds
          s.defaults = mergeDefaults(j);                            // …and what the server applies when unset
          model[alias] = adaptServerRows(j);                        // dataset → data.<name> = the rows array
        }
        return model[alias];
      }, function () { return model[alias]; }); // graceful: keep the last render on error
    }
    function refetchDatasetSources() { Object.keys(sources).forEach(function (n) { if (sources[n].kind !== 'external') fetchSource(n); }); }
    // A page that SHOWS what it collects (a poll's live tally, a sign-up count) must count the
    // visitor's own answer. Nothing re-read it after a save, so the vote landed and the results
    // on the same page stayed as they were (2026-09-29). Once a write is durable, read every
    // dataset source again. Debounced: a burst of saves (a quiz saving each answer) costs one.
    var refetchTimer = null;
    function refetchAfterSave() {
      var any = Object.keys(sources).some(function (n) { return sources[n].kind !== 'external' && sources[n].readKey; });
      if (!any) return;
      if (refetchTimer) clearTimeout(refetchTimer);
      refetchTimer = setTimeout(function () { refetchTimer = null; refetchDatasetSources(); }, 150);
    }
    var intervals = []; // tracked for destroy() + removeSource (no leaked polls)
    function scheduleRefresh(alias, refresh) {
      if (!refresh || refresh <= 0) return;
      var timer = setInterval(function () { if (!(global.document && document.hidden)) fetchSource(alias); }, Math.max(refresh, 10) * 1000);
      intervals.push(timer); sources[alias].__timer = timer;
    }
    // ── imperative source lifecycle — REAL JS calls (boot() and the author use these) ──
    function addSource(alias, descriptor) { // fetch + register; returns Promise<data>
      var prev = sources[alias];
      if (prev && prev.__timer) { clearInterval(prev.__timer); var pi = intervals.indexOf(prev.__timer); if (pi >= 0) intervals.splice(pi, 1); }
      sources[alias] = normalizeSource(descriptor);
      scheduleRefresh(alias, sources[alias].refresh);
      return fetchSource(alias);
    }
    function refetch(alias) { return fetchSource(alias); } // Promise<data>, reactively refills data.<alias>
    function removeSource(alias) {
      var s = sources[alias]; if (!s) return;
      if (s.__timer) { clearInterval(s.__timer); var i = intervals.indexOf(s.__timer); if (i >= 0) intervals.splice(i, 1); }
      delete sources[alias]; delete model[alias];
    }
    var onVis = function () { if (!document.hidden) Object.keys(sources).forEach(fetchSource); };
    var onOnline = function () { Object.keys(sources).forEach(fetchSource); };
    if (global.document) document.addEventListener('visibilitychange', onVis);
    if (global.addEventListener) global.addEventListener('online', onOnline);

    // ── x-model breadth: text/number/checkbox/checkbox-group/radio/select/select-multiple/date ──
    /** How many checkboxes (this one included) share `el`'s group: same form (or the whole
     *  document for a box outside any form), and the same name or the same model path. All
     *  of a group's boxes are in the parsed DOM before the first is wired, so the count is
     *  settled when it is asked. */
    function checkboxPeers(el, name, path) {
      var scope = el.form || (el.closest && el.closest('form')) || el.ownerDocument || document;
      var boxes = scope.querySelectorAll ? scope.querySelectorAll('input[type="checkbox"]') : [];
      var n = 0;
      for (var i = 0; i < boxes.length; i++) {
        var b = boxes[i];
        if ((name && b.getAttribute('name') === name) || b.getAttribute('data-pd-model') === path) n++;
      }
      return n;
    }
    function wireModel(el) {
      var path = el.getAttribute('data-pd-model');
      if (!path) { warn('data-pd-model with empty path on <' + el.tagName.toLowerCase() + '>'); return; }
      if (DBG) { var _root = String(path).split('.')[0]; if (model[_root] === undefined || model[_root] === null) dlog('⚠⚠ wireModel: model root "' + _root + '" is MISSING when wiring "' + path + '" — the AUTHORED boot did NOT provide this slice, so DOM-wiring is about to CREATE ITS OWN empty object. THIS is the corruption. (constants lost)'); }
      var mods = modsOf(el, 'data-pd-model');
      // A MODIFIER WRITTEN INTO THE PATH — `data-pd-model="rsvp.guestCount.number"`, the Vue habit
      // (`v-model.number`) carried into the value. Read as a path, typing 3 wrote
      // `rsvp.guestCount = { number: "3" }` over the declared number, so `Number(guestCount)` was NaN
      // and a generated RSVP refused every submission (create-flow run 67, 2026-09-30). The tail is a
      // modifier only when it cannot be a field: its parent already holds a plain value (a primitive
      // has no fields; `null` is how a number field is often declared) that the modifier fits
      // (`modFitsValue`). A real `phone.number` under an object, or undeclared, stays a path.
      var tailAt = path.lastIndexOf('.');
      if (tailAt > 0) {
        var tailMod = path.slice(tailAt + 1);
        if (MODEL_MOD_NAMES.indexOf(tailMod) >= 0) {
          var parentVal = getPath(model, path.slice(0, tailAt));
          if (modFitsValue(tailMod, parentVal, el)) {
            warn('data-pd-model "' + path + '": "' + tailMod + '" is a modifier, not a field — read as data-pd-model="' + path.slice(0, tailAt) + '" data-pd-model-mods=".' + tailMod + '"');
            path = path.slice(0, tailAt);
            if (mods.indexOf(tailMod) < 0) mods = mods.concat([tailMod]);
          }
        }
      }
      var tag = el.tagName, type = (el.type || '').toLowerCase();
      var isCheck = type === 'checkbox', isRadio = type === 'radio', isMulti = tag === 'SELECT' && el.multiple;
      var groupName = el.getAttribute('name'); // checkbox/radio groups share a model path
      // checkbox is ARRAY-mode when it is one of a GROUP — two or more checkboxes in the same
      // form sharing its name or its model path — or when the bound model is already an array
      // (Alpine's rule). Otherwise it is a single BOOLEAN.
      //
      // Having a `name` is NOT a group. Every field in a form is named, and a lone consent box
      // named "consent" was put in array mode, so ticking it stored ["on"]: the page checked
      // `consent === true` and every parent who gave permission was recorded as NOT consenting
      // (trust audit 2026-09-28, a real field-trip slip — CONSENT ✗ for all four parents).
      var arrayCheck = isCheck && (checkboxPeers(el, groupName, path) > 1 || Array.isArray(getPath(model, path)));
      function coerce(v) {
        if (mods.indexOf('number') >= 0) { if (v === '') return null; var num = Number(v); return Number.isNaN(num) ? null : num; }
        if (mods.indexOf('boolean') >= 0) return v === true || v === '1' || v === 'true' || v === 'on' || v === 'yes';
        if (mods.indexOf('trim') >= 0) return String(v).trim();
        return v;
      }
      var ev = (tag === 'SELECT' || isCheck || isRadio) ? 'change' : (mods.indexOf('lazy') >= 0 ? 'change' : 'input');
      var handler = function () {
        if (isRadio) { if (el.checked) setPath(model, path, coerce(el.value)); return; }
        if (arrayCheck) { // checkbox GROUP / array → array
          var cur = getPath(model, path); cur = Array.isArray(cur) ? cur.slice() : [];
          var i = cur.indexOf(el.value); if (el.checked && i < 0) cur.push(el.value); if (!el.checked && i >= 0) cur.splice(i, 1);
          setPath(model, path, cur); return;
        }
        if (isCheck) { setPath(model, path, el.checked); return; }
        if (isMulti) { setPath(model, path, Array.from(el.selectedOptions).map(function (o) { return o.value; })); return; }
        setPath(model, path, coerce(el.value));
      };
      // .fill / bake-static DEFAULT: when the model has NO value yet, SEED it from
      // the element's current (server-baked) DOM value, so the model mirrors what's
      // shown and submission sends the right thing. (Deliberate divergence from
      // Alpine, whose model is source-of-truth by default — our baked DOM is.)
      // Array-checkboxes always run so every checked box in a group contributes.
      // …but a value REHYDRATED from a saved draft is authoritative — don't let an
      // array-checkbox group re-seed from the (unchecked) DOM and wipe the restored array.
      if (getPath(model, path) === undefined || (arrayCheck && !rehydratedPaths[path])) {
        if (!isRadio || el.checked) handler();
      }
      // THE MODEL IS WRITTEN BEFORE THE PAGE'S OWN LISTENERS RUN. A page's script sits at the end of
      // <body> and runs before the runtime wires anything, so a listener it put on the SAME element
      // used to run FIRST and read the old value: a history quiz enabled its Next and Submit buttons
      // from `app.data.quizDraft[id]` inside its own `change` handler, read "" every time, and could
      // never be submitted (create-flow run 62, 2026-09-30). Vue gives `v-model` this order; so do we,
      // by listening in the CAPTURE phase at the document, which runs before any target or bubble
      // listener the page added to the element or its ancestors.
      var onModelEvent = mods.indexOf('debounce') >= 0 ? debounce(handler, 300) : handler;
      listen(el, el.ownerDocument || document, ev, function (e) { if (e.target === el) onModelEvent(e); }, true);

      // PROGRAMMATIC WRITES (`el.value = …` from the page's own script) must reach the model.
      //
      // Assigning `.value` in JS fires NO input/change event, so the listener above never runs.
      // A page that computes a payload and parks it in a hidden field — the shape a generator
      // reaches for constantly — therefore saved the model's INITIAL value instead of the
      // computed one. Worse, the reactive effect below then wrote the stale model value straight
      // back over the assignment, so the page's own data was actively erased. Live example:
      // `responses` bound to an `sr-only` textarea, filled with `JSON.stringify(...)` at submit,
      // and saved as `""` (prod, 2026-08-14).
      //
      // Wrapping the element's own `value`/`checked` accessor routes an assignment through the
      // same handler a keystroke uses, so both paths agree. `selfWrite` keeps the runtime's own
      // write-back below from re-entering the handler.
      var selfWrite = false;
      (function interceptProgrammaticWrites() {
        var proto = tag === 'TEXTAREA' ? window.HTMLTextAreaElement && window.HTMLTextAreaElement.prototype
          : tag === 'SELECT' ? window.HTMLSelectElement && window.HTMLSelectElement.prototype
            : window.HTMLInputElement && window.HTMLInputElement.prototype;
        if (!proto) return;
        // `checked` for the box/radio kinds, `value` for everything else — whichever one the
        // handler above actually reads, so the two stay in step.
        var prop = (isCheck || isRadio) ? 'checked' : 'value';
        var d = Object.getOwnPropertyDescriptor(proto, prop);
        if (!d || !d.get || !d.set) return;               // exotic host object — leave it alone
        if (Object.getOwnPropertyDescriptor(el, prop)) return; // already wrapped (re-wire)
        try {
          Object.defineProperty(el, prop, {
            configurable: true,
            enumerable: false,
            get: function () { return d.get.call(this); },
            set: function (v) { d.set.call(this, v); if (!selfWrite) handler(); },
          });
        } catch (e) { /* a frozen/sealed element: the keystroke path still works */ }
      })();

      bind(el, function () {
        var v = getPath(model, path);
        selfWrite = true;
        try {
          if (isRadio) el.checked = String(el.value) === String(v);
          // The SAME list-mode decision the click handler makes (`arrayCheck`), never `name` alone:
          // keyed on `name`, an unnamed group over [] showed every box ticked (`!![]`) and a lone
          // named consent box unticked itself after a click (2026-09-29).
          else if (isCheck && arrayCheck) el.checked = Array.isArray(v) && v.indexOf(el.value) >= 0;
          else if (isCheck) el.checked = !!v;
          else if (isMulti) { var set = Array.isArray(v) ? v : []; Array.from(el.options).forEach(function (o) { o.selected = set.indexOf(o.value) >= 0; }); }
          else { var s = v == null ? '' : String(v); if (el.value !== s) el.value = s; }
        } finally { selfWrite = false; }
      });
    }

    // ── keyed reconciliation (data-pd-each) — reuse nodes, preserve focus/state ──
    // BIDIRECTIONAL keyed list. Renders model→DOM (keyed reconciliation, node
    // reuse) AND wires DOM→model per row: a `data-pd-field` on an INPUT/SELECT/
    // TEXTAREA two-way-binds to item[field] (editing a row updates the model),
    // and `data-pd-remove` splices THIS item out of the model list. Each row
    // carries `__pdItem` = its current item, so the per-row handlers always
    // target the live item even after reuse/reorder.
    function wireEach(el) {
      var path = el.getAttribute('data-pd-each');
      var keyField = el.getAttribute('data-pd-key');
      var tpl = el.firstElementChild ? el.firstElementChild.cloneNode(true) : document.createElement('div');
      el.innerHTML = '';
      var byKey = Object.create(null);
      function keyOf(item, idx) { return String(keyField ? item[keyField] : idx); }
      function liveList() { var l = getPath(model, path); if (l != null && !Array.isArray(l)) warn('data-pd-each "' + path + '" is not an array — skipped'); return Array.isArray(l) ? l : []; }
      var current = []; // node array from the previous render — input to udomdiff
      bind(el, function () {
        var full = liveList();
        // OOM BACKSTOP: never materialize more than MAX_ROWS DOM nodes. The model
        // keeps the full list (submission/mutations use liveList()); only the RENDER
        // is capped. Large lists must be paginated/aggregated server-side.
        var list = full.length > MAX_ROWS ? full.slice(0, MAX_ROWS) : full;
        if (full.length > MAX_ROWS) warn('data-pd-each "' + path + '" has ' + full.length + ' items — rendering the first ' + MAX_ROWS + '. Paginate/aggregate server-side (the /ds query should return bounded rows).');
        var seen = Object.create(null);
        // Build the ordered node array: reuse a node by key (keeps focus/state),
        // clone the template for a new key, patch every field in place.
        var next = list.map(function (item, idx) {
          var key = keyOf(item, idx); if (seen[key]) warn('data-pd-each duplicate key "' + key + '" — rows will collide'); seen[key] = 1;
          var row = byKey[key]; var isNew = !row;
          if (isNew) { row = tpl.cloneNode(true); byKey[key] = row; }
          row.__pdItem = item; // live item for this key (per-row handlers read this)
          // PERF: query the row's fields ONCE (at clone time) and reuse the cached
          // list every render — no querySelectorAll per row per frame on big lists.
          var fields = row.__pdFields || (row.__pdFields = qsa(row, '[data-pd-field]'));
          fields.forEach(function (f) {
            var k = f.getAttribute('data-pd-field'); var v = item[k];
            var input = f.tagName === 'INPUT' || f.tagName === 'SELECT' || f.tagName === 'TEXTAREA';
            if (input) {
              if (f.type === 'checkbox') { if (f.checked !== !!v) f.checked = !!v; }
              else { var s = v == null ? '' : String(v); if (f.value !== s) f.value = s; }
              if (isNew) { var ev = (f.tagName === 'SELECT' || f.type === 'checkbox') ? 'change' : 'input'; f.addEventListener(ev, function () { var it = row.__pdItem; if (it) it[k] = f.type === 'checkbox' ? f.checked : f.value; }); }
            } else { var t = fmt(v); if (f.textContent !== t) f.textContent = t; }
          });
          if (isNew) { // per-row delete → splice this item out of the model list
            var rm = row.querySelector('[data-pd-remove]');
            if (rm) rm.addEventListener('click', function (e) {
              e.preventDefault(); var lst = liveList(); var it = row.__pdItem; var i = lst.indexOf(it);
              if (i < 0) for (var j = 0; j < lst.length; j++) { if (keyOf(lst[j], j) === key) { i = j; break; } }
              if (i >= 0) lst.splice(i, 1);
            });
          }
          return row;
        });
        // MINIMAL-MOVE reconciliation via udomdiff (the benchmarked keyed-list
        // differ behind µhtml) — optimal insert/move/remove. Falls back to a
        // simple rebuild if the lib somehow isn't loaded.
        if (global.udomdiff) global.udomdiff(el, current, next, function (n) { return n; }, null);
        else { el.textContent = ''; next.forEach(function (n) { el.appendChild(n); }); }
        current = next;
        Object.keys(byKey).forEach(function (k) { if (!seen[k]) delete byKey[k]; }); // drop stale keys (udomdiff removed the nodes)
      });
    }

    // ── data-pd-bind: a FILTER control → model.params.<axis> → refetch every dataset ──
    // ONE directive, EVERY control type (spec §Controls). The type is data-pd-control
    // ("select|radio|checkbox|buttons|range|date|search") or inferred from the element.
    // GROUP types (radio/checkbox/buttons) render their choices from the source's
    // domains.<axis> when the author left the container empty, else wire the author's
    // own children as-is. checkbox = MULTI (params.<axis> is an array); the rest single.
    function bindKind(el) {
      var c = (el.getAttribute('data-pd-control') || '').toLowerCase();
      if (c) return c;
      var tag = el.tagName, type = (el.type || '').toLowerCase();
      if (tag === 'SELECT') return 'select';
      if (tag === 'INPUT') return type === 'range' ? 'range' : type === 'date' ? 'date' : type === 'checkbox' ? 'checkbox' : type === 'radio' ? 'radio' : 'search';
      return 'buttons'; // a bare container defaults to a segmented button group
    }
    function bindDomain(el, axis) { var src = sources[el.getAttribute('data-pd-from') || Object.keys(sources)[0]]; return (src && src.domains && src.domains[axis]) || null; }
    function bindRange(el, axis) { var src = sources[el.getAttribute('data-pd-from') || Object.keys(sources)[0]]; return (src && src.ranges && src.ranges[axis]) || null; }
    function bindDefault(el, axis) { var src = sources[el.getAttribute('data-pd-from') || Object.keys(sources)[0]]; return (src && src.defaults && src.defaults[axis] != null) ? src.defaults[axis] : ''; }
    function bindButtonVal(b) { var v = b.getAttribute('data-value'); return v != null ? v : (b.getAttribute('value') != null ? b.getAttribute('value') : (b.textContent || '').trim()); }
    function wireBind(el) {
      var axis = el.getAttribute('data-pd-bind'); if (!axis) { warn('data-pd-bind with empty axis on <' + el.tagName.toLowerCase() + '>'); return; }
      var mods = modsOf(el, 'data-pd-bind'), kind = bindKind(el), pkey = 'params.' + axis;
      var isGroup = kind === 'radio' || kind === 'checkbox' || kind === 'buttons';
      function readValue() {
        if (kind === 'buttons') { var bs = qsa(el, 'button'); for (var i = 0; i < bs.length; i++) if (bs[i].getAttribute('aria-pressed') === 'true') return bindButtonVal(bs[i]); return ''; }
        if (kind === 'radio' && isGroup && el.tagName !== 'INPUT') { var rs = qsa(el, 'input'); for (var j = 0; j < rs.length; j++) if (rs[j].checked) return rs[j].value; return ''; }
        if (kind === 'checkbox' && el.tagName !== 'INPUT') { var cs = qsa(el, 'input'), out = []; for (var k = 0; k < cs.length; k++) if (cs[k].checked && cs[k].value !== '') out.push(cs[k].value); return out; }
        if (el.tagName === 'INPUT' && (el.type || '').toLowerCase() === 'checkbox') return el.checked ? el.value : '';
        if (el.tagName === 'SELECT' && el.multiple) return Array.from(el.selectedOptions).map(function (o) { return o.value; });
        return el.value;
      }
      function apply() {
        var v = readValue();
        if (mods.indexOf('number') >= 0 && !Array.isArray(v)) { v = v === '' ? '' : (isFinite(Number(v)) ? Number(v) : v); }
        setPath(model, pkey, v); refetchDatasetSources();
      }
      if (getPath(model, pkey) === undefined) setPath(model, pkey, readValue()); // baked DOM seeds params
      // WIRE the change/click event.
      if (kind === 'buttons') {
        listen(el, el, 'click', function (ev) { var t = ev.target; var b = t && t.closest ? t.closest('button') : null; if (!b || !el.contains(b)) return; ev.preventDefault(); qsa(el, 'button').forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); }); apply(); });
      } else {
        var evName = (el.tagName === 'SELECT' || kind === 'radio' || kind === 'checkbox' || kind === 'date') ? 'change' : 'input';
        listen(el, el, evName, mods.indexOf('debounce') >= 0 ? debounce(apply, 300) : apply);
      }
      // REACTIVE fill: when the source's domain arrives, GENERATE the choices — but only
      // if the author left the control empty (else respect the author's own options).
      bind(el, function () {
        // REACTIVE DEPENDENCY, load-bearing. The domain lives on the SOURCE REGISTRY
        // (`s.domains`), which is a plain object — assigning to it triggers nothing. So
        // this effect ran once at wire time, before the first fetch had resolved, found
        // no domain, and never ran again: every generated filter rendered with zero
        // options while the server was returning them (2026-09-04). Touching the
        // source's ROWS — which ARE reactive, and are swapped in immediately AFTER
        // `s.domains` is set — makes the effect re-run at the moment the domain exists.
        var fromName = el.getAttribute('data-pd-from') || Object.keys(sources)[0];
        if (fromName) void model[fromName];
        // BOUNDS first: a date/number/range filter carries a `range`, never a `domain`, so
        // it would never survive the domain guard below. The server was already sending
        // these and nothing read them, leaving date pickers unclamped (2026-09-04).
        var rng = bindRange(el, axis);
        if (rng && (kind === 'date' || kind === 'range' || el.tagName === 'INPUT')) {
          if (el.min !== String(rng.min)) el.min = rng.min;
          if (el.max !== String(rng.max)) el.max = rng.max;
        }
        var domain = bindDomain(el, axis); if (!domain || !domain.length) return;
        // THE CONTROL SHOWS THE STATE THE DATA IS IN. A generated select used to get one option
        // per value and nothing else, so the browser showed the FIRST store while the filter
        // was unset: the panels held every store's totals (or, before the server read "" as
        // unset, nothing at all) under one store's name, and once a store was picked there was
        // no way back to all of them (2026-09-29). So a single-choice control whose default is
        // blank opens on an "All" choice, and one with a real default opens on that default:
        // exactly what the server applies when the page sends no value.
        var dflt = bindDefault(el, axis), cur = getPath(model, pkey);
        var shown = (cur != null && cur !== '' && !Array.isArray(cur)) ? String(cur) : dflt;
        if (kind === 'select') {
          if (el.tagName === 'SELECT' && el.options.length <= 1) {
            if (!el.options.length && !el.multiple && dflt === '') { var all = document.createElement('option'); all.value = ''; all.textContent = allLabel(); el.appendChild(all); }
            domain.forEach(function (v) { var o = document.createElement('option'); o.value = v; o.textContent = v; el.appendChild(o); });
            if (!el.multiple) el.value = shown;
          }
          return;
        }
        if (kind === 'range') { var ns = domain.map(Number).filter(isFinite); if (ns.length) { el.min = Math.min.apply(null, ns); el.max = Math.max.apply(null, ns); } return; }
        // A single-choice GROUP gets the same "All" choice, first, for the same reason. A
        // checkbox group is multi-choice: nothing ticked already reads as "all".
        var choices = (kind !== 'checkbox' && dflt === '') ? [''].concat(domain) : domain;
        if (kind === 'radio' || kind === 'checkbox') {
          if (qsa(el, 'input').length) return; // author provided — leave it
          choices.forEach(function (v) {
            var lab = document.createElement('label'); lab.style.cssText = 'display:inline-flex;gap:4px;align-items:center;margin-right:10px';
            var inp = document.createElement('input'); inp.type = kind; inp.value = v; if (kind === 'radio') inp.name = 'pd-bind-' + axis;
            if (kind === 'radio' ? shown === v : (Array.isArray(cur) && cur.indexOf(v) >= 0)) inp.checked = true;
            var sp = document.createElement('span'); sp.textContent = v === '' ? allLabel() : v; lab.appendChild(inp); lab.appendChild(sp); el.appendChild(lab);
          });
          return;
        }
        if (kind === 'buttons') {
          if (qsa(el, 'button').length) return; // author provided — leave it
          choices.forEach(function (v) { var b = document.createElement('button'); b.type = 'button'; b.setAttribute('data-value', v); b.setAttribute('aria-pressed', shown === v ? 'true' : 'false'); b.textContent = v === '' ? allLabel() : v; el.appendChild(b); });
        }
      });
    }

    // ── views: Chart.js (charts) + HTML (table/number) ──
    function renderView(el, rows, viz, label) {
      rows = rows || [];
      if (viz === 'number') { var r0 = rows[0]; el.textContent = fmtMetric(r0 ? (r0.value != null ? r0.value : r0.y) : undefined); return; }
      if (viz === 'table') {
        // Build the table via the DOM (createElement + textContent). The browser
        // escapes every value natively — NO hand-rolled HTML escaping, and HTML
        // injection from dataset rows is impossible by construction (owner 2026-07-12).
        if (el.__chart) { try { el.__chart.destroy(); } catch (e) {} el.__chart = null; }
        var mk = function (tag, text, css) { var e = document.createElement(tag); if (text != null) e.textContent = text; if (css) e.style.cssText = css; return e; };
        var table = mk('table', null, 'width:100%;border-collapse:collapse');
        var htr = document.createElement('tr');
        htr.appendChild(mk('th', 'Label', 'text-align:left;padding:6px 10px;border-bottom:1px solid #e5e7eb'));
        htr.appendChild(mk('th', 'Value', 'text-align:right;padding:6px 10px;border-bottom:1px solid #e5e7eb'));
        var thead = document.createElement('thead'); thead.appendChild(htr); table.appendChild(thead);
        var tbody = document.createElement('tbody');
        rows.slice(0, MAX_ROWS).forEach(function (r) {
          var tr = document.createElement('tr');
          tr.appendChild(mk('td', fmt(r.label), 'padding:6px 10px;border-bottom:1px solid #f1f5f9'));
          tr.appendChild(mk('td', fmt(r.value), 'padding:6px 10px;text-align:right;border-bottom:1px solid #f1f5f9'));
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);
        el.textContent = ''; el.appendChild(table);
        return;
      }
      if (!global.Chart) return;
      var canvas = el.querySelector('canvas');
      if (!canvas) {
        // A SIZED BOX of its own, not the figure. Chart.js (responsive, no fixed aspect) takes
        // its HEIGHT from the canvas's parent. A figure sized by its content (a grid row of
        // `minmax(28px,auto)`, a caption above the chart, padding) measures a height that
        // includes the chart, draws taller, and the figure grows to fit: a feedback loop that
        // ran every chart on a generated dashboard past 4,800px (2026-09-29). A box with a
        // definite height ends it; a page can size its charts with `--pd-chart-h`.
        var box = document.createElement('div'); box.className = 'pd-chart-box';
        box.style.cssText = 'position:relative;width:100%;height:var(--pd-chart-h,clamp(220px,32vw,360px))';
        canvas = document.createElement('canvas'); box.appendChild(canvas); el.appendChild(box);
      }
      var cfg = chartConfigFor(rows, viz, label);
      if (el.__chart && el.__chart.config.type === cfg.type) { // UPDATE in place (Alpine-parity: don't rebuild)
        el.__chart.data = cfg.data; el.__chart.options = cfg.options; el.__chart.update();
      } else {
        if (el.__chart) el.__chart.destroy();
        el.__chart = new global.Chart(canvas.getContext('2d'), cfg);
      }
    }

    // ── directive scan (idempotent + re-entrant; skips [data-pd-ignore] subtrees) ──
    // PER-DIRECTIVE idempotency (an element can carry many directives, e.g. a
    // button with both data-pd-attr AND data-pd-save). Tracked on a JS property
    // (not a DOM attribute) so the flag never serializes into saved HTML and a
    // body-swap's fresh nodes are naturally re-scanned.
    function each(root, sel, fn) {
      var els = qsa(root, sel);
      // querySelectorAll matches DESCENDANTS only — a dynamically-added node that is
      // ITSELF a directive root (e.g. the observer hands us an appended <ul data-pd-each>)
      // would be missed. Include the root when it matches so live-inserted roots wire.
      if (root.nodeType === 1 && root.matches && root.matches(sel)) els = [root].concat(els);
      els.forEach(function (el) { if (ignored(el)) return; if (!el.__pdw) el.__pdw = {}; if (el.__pdw[sel]) return; el.__pdw[sel] = 1; try { fn(el); } catch (e) { logErr('wire ' + sel, e); } });
    }
    // A MODIFIER WRITTEN INTO THE ATTRIBUTE NAME (pages audit 2026-10-04). A generated pre-order
    // form bound its quantities as `data-pd-model.number="order.qty"` — Vue's `v-model.number`
    // habit. That is a different attribute from `data-pd-model`, so nothing bound, the summary
    // stayed empty and the form refused every submission. The intent is unambiguous (the name is
    // a directive this runtime owns plus registry modifiers), so read it as the canonical pair:
    // `data-pd-model="order.qty"` + `data-pd-model-mods=".number"`. Only an exact directive name
    // and only known modifiers are adopted; anything else stays an unknown marker (DEV warning).
    var DOTTED_MODS = { 'data-pd-model': MODEL_MOD_NAMES, 'data-pd-bind': ['debounce', 'number'], 'data-pd-save': ['debounce', 'throttle', 'once'], 'data-pd-query': ['debounce', 'throttle', 'once'] };
    function dottedParts(n) {
      var i = n.indexOf('.'); if (i < 0) return null;
      var dir = n.slice(0, i), allowed = DOTTED_MODS[dir]; if (!allowed) return null;
      var mods = n.slice(i + 1).split('.');
      for (var k = 0; k < mods.length; k++) if (allowed.indexOf(mods[k]) < 0) return null;
      return { dir: dir, mods: mods };
    }
    function adoptDottedMods(root) {
      var list = [root].concat(qsa(root, '*'));
      list.forEach(function (el) {
        var at = el.attributes; if (!at) return;
        for (var i = 0; i < at.length; i++) {
          var n = at[i].name; if (n.indexOf('data-pd-') !== 0 || n.indexOf('.') < 0) continue;
          var d = dottedParts(n); if (!d || el.hasAttribute(d.dir)) continue;
          var have = modsOf(el, d.dir);
          d.mods.forEach(function (m) { if (have.indexOf(m) < 0) have.push(m); });
          el.setAttribute(d.dir + '-mods', '.' + have.join('.'));
          el.setAttribute(d.dir, at[i].value);
        }
      });
    }
    function scan(root) {
      root = root || document.body; if (root.nodeType !== 1) return;
      adoptDottedMods(root);
      // NOTE: sources are NOT scanned from the DOM — they're declared imperatively via
      // boot({ sources }) / addSource() (config lives in JS, not a deletable div). scan()
      // only wires the BINDING/VIEW markers below to the model those calls populate.
      each(root, '[data-pd-text]', function (el) { var p = el.getAttribute('data-pd-text'); if (!p) { warn('data-pd-text with empty path on <' + el.tagName.toLowerCase() + '>'); return; } var fspec = el.getAttribute('data-pd-format'); bind(el, function () { var raw = getPath(model, p); el.textContent = fspec ? formatValue(raw, fspec) : fmt(raw); }); });
      each(root, '[data-pd-show]', function (el) {
        var p = el.getAttribute('data-pd-show'); if (!p) { warn('data-pd-show empty path'); return; }
        // Reveal RESPECTS the cascade — clearing inline display so responsive `@media` and class
        // `display` rules keep working (the normal case). But it also GUARANTEES visibility: if
        // AFTER that the element is STILL computed `display:none`, a CSS RULE pinned it there —
        // e.g. `.card{display:none}` + a `.visible{display:block}` toggle an author wrote out of
        // habit — so we override with a forced `display` (!important). Without this a wired
        // confirmation could stay invisible even though `_status.<name>Saved` is true, because
        // clearing INLINE display can't beat a class rule. (Vue v-show / Alpine x-show don't do
        // this — the exact gap.) `block` is the sensible default for the pinned-none case.
        bind(el, function () {
          if (getPath(model, p)) {
            el.style.display = '';                                                  // respect the cascade first
            var stuck = false; try { stuck = getComputedStyle(el).display === 'none'; } catch (e) {}
            if (stuck) el.style.setProperty('display', 'block', 'important');        // a CSS rule forces none → override
          } else {
            el.style.display = 'none';
          }
        });
      });
      each(root, '[data-pd-if]', function (el) { // real insert/remove via an anchor comment
        var p = el.getAttribute('data-pd-if'); if (!p) { warn('data-pd-if empty path'); return; } var parent = el.parentNode; var anchor = document.createComment('pd-if'); parent.insertBefore(anchor, el); var attached = true;
        // The toggled element is pd-MANAGED: cleanup() skips it, so detaching it
        // (a normal x-if op) does NOT stop the controlling effect or its inner
        // bindings. The controller lives on the PARENT (persistent), so it
        // survives the child's removal and can re-attach when the value flips.
        el.__pdManaged = 1;
        var runner = rawEffect(function () { var on = !!getPath(model, p); if (on && !attached) { anchor.parentNode.insertBefore(el, anchor.nextSibling); attached = true; } else if (!on && attached) { el.parentNode.removeChild(el); attached = false; } }, { scheduler: function () { enqueue(runner); } });
        addDisposer(parent, function () { stop(runner); });
      });
      each(root, '[data-pd-attr]', function (el) { // "disabled:inputs.busy, href:inputs.url"
        var specs = el.getAttribute('data-pd-attr').split(',').map(function (s) { return s.trim(); }).filter(Boolean)
          .map(function (s) { var i = s.indexOf(':'); if (i < 0) { warn('data-pd-attr spec missing ":" — "' + s + '"'); return null; } return { a: s.slice(0, i).trim(), p: s.slice(i + 1).trim() }; }).filter(Boolean)
          // SECURITY: never let bound DATA become CODE. Refuse event-handler attrs
          // (on*) outright — a page can't wire `onclick` from a model value. URL
          // attrs are allowed but sanitized at set-time (javascript:/vbscript:).
          .filter(function (x) { if (/^on/i.test(x.a) || x.a.toLowerCase() === 'srcdoc') { warn('data-pd-attr refused "' + x.a + '" (would let data become code)'); return false; } return true; });
        bind(el, function () {
          specs.forEach(function (x) {
            var v = getPath(model, x.p);
            if (x.a === 'disabled' || x.a === 'checked' || x.a === 'hidden' || x.a === 'readonly' || x.a === 'required') { if (v) el.setAttribute(x.a, ''); else el.removeAttribute(x.a); return; }
            if (v == null || v === false) { el.removeAttribute(x.a); return; }
            // URL-bearing attrs -> the AUDITED @braintree/sanitize-url (vendored):
            // neutralizes javascript:/data:/vbscript: incl. HTML-encoded + control-
            // char obfuscation -> 'about:blank'. A control-char-normalized scheme
            // regex is the fallback if the lib somehow isn't loaded.
            if (URL_ATTRS[x.a.toLowerCase()]) {
              var clean = global.PDSanitizeUrl ? global.PDSanitizeUrl.sanitizeUrl(String(v)) : (/^(?:javascript|vbscript|data:text\/html)/i.test(String(v).replace(/[\u0000-\u0020]+/g, '')) ? 'about:blank' : String(v));
              if (clean === 'about:blank' && String(v).toLowerCase().indexOf('about:blank') !== 0) { warn('data-pd-attr blocked unsafe URL on "' + x.a + '"'); el.removeAttribute(x.a); return; }
              el.setAttribute(x.a, clean); return;
            }
            el.setAttribute(x.a, v);
          });
        });
      });
      each(root, '[data-pd-class]', function (el) { // "active:params.regionActive, warn:inputs.bad"
        var specs = el.getAttribute('data-pd-class').split(',').map(function (s) { return s.trim(); }).filter(Boolean).map(function (s) { var i = s.indexOf(':'); if (i < 0) { warn('data-pd-class spec missing ":" — "' + s + '"'); return null; } return { c: s.slice(0, i).trim(), p: s.slice(i + 1).trim() }; }).filter(Boolean);
        bind(el, function () { specs.forEach(function (x) { toggleClass(el, x.c, !!getPath(model, x.p)); }); });
      });
      each(root, '[data-pd-model]', wireModel);
      each(root, '[data-pd-each]', wireEach);
      each(root, '[data-pd-bind]', wireBind); // filter control → params.<axis> → refetch (all control types)
      each(root, '[data-pd-dataset]', function (el) { // a Chart.js/HTML view of data.<name> (the rows array a read filled)
        var name = el.getAttribute('data-pd-dataset'), label = el.getAttribute('data-pd-label');
        bind(el, function () { var viz = el.getAttribute('data-pd-viz') || 'bar'; var rows = model[name]; var _p = model.params; void _p; if (Array.isArray(rows)) renderView(el, rows, viz, label); });
      });
      // SAVE — bound by DELEGATION in the CAPTURE phase, not per node (owner 2026-08-05).
      //
      // A per-node bubble-phase listener gave the page three ways to silently destroy a capture,
      // all of them observed in generated pages:
      //   • re-render — `innerHTML = …` replaces the button and the listener dies with the node;
      //   • stopPropagation — the page's own click handler runs FIRST and the save never bubbles;
      //   • disabled — a browser fires no click at all on a disabled control (see the observer
      //     below, which is the only part delegation cannot solve).
      // One capture-phase listener on the root fixes the first two by construction: it matches the
      // element at CLICK time via closest(), so a replacement element is bound the moment it
      // exists, and capture runs before any page handler can swallow the event.
      //
      // The per-element handler is still built ONCE and cached on the node, so `.once` and the
      // other data-pd-save-mods keep their semantics per control rather than resetting each click.
      /**
       * Hand any pending recordings to the server, and replace each one in the payload
       * with the handle it comes back as.
       *
       * Rides the SAME write key as the row, because the clip is part of the row — the
       * page has exactly one grant and uses it for both. Resolves even when every upload
       * fails: an answer sheet that submits without one audio field beats one that does
       * not submit at all.
       */
      function uploadRecordings(writeKey, slice) {
        var pending = [];
        var nodes = root.querySelectorAll ? root.querySelectorAll('[data-pd-audio="record"]') : [];
        for (var i = 0; i < nodes.length; i++) {
          var el = nodes[i];
          var field = (el.getAttribute('data-pd-field') || '').trim();
          if (!field || !el.__pdBlob || el.__pdUploaded === el.__pdBlob) continue;
          pending.push({ el: el, field: field, blob: el.__pdBlob });
        }
        if (!pending.length || !slice || typeof slice !== 'object') return Promise.resolve(slice);

        return Promise.all(pending.map(function (p) {
          return blobToBase64(p.blob).then(function (b64) {
            return apiFetch('/api/v1/ds/' + encodeURIComponent(writeKey) + '/audio', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ audio: b64 }),
            }).then(function (r) { return r && r.ok ? r.json() : null; });
          }).then(function (j) {
            if (j && j.clipId) {
              slice[p.field] = j.clipId;
              p.el.__pdUploaded = p.blob;   // never upload the same take twice
            }
          }).catch(function () { /* field stays empty; the rest of the answers still go */ });
        })).then(function () { return slice; });
      }

      function blobToBase64(blob) {
        return new Promise(function (resolve, reject) {
          var fr = new FileReader();
          fr.onload = function () { resolve(String(fr.result).split(',')[1] || ''); };
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
      }

      /**
       * Record HOW THE READER USED THE AUDIO on the submission itself.
       *
       * A "played once" listening test that cannot show it was played once is not
       * defensible, so the evidence travels with the attempt. Only added when the page
       * actually has audio, and never overwrites a value the page set for itself — an
       * app that computes its own listening record knows more than we do.
       */
      function attachAudioTelemetry(slice) {
        if (!slice || typeof slice !== 'object' || Array.isArray(slice)) return;
        if (slice.audioPlayback != null) return;
        var audio = window.PD && window.PD.audio;
        if (!audio || typeof audio.telemetry !== 'function') return;
        try {
          var t = audio.telemetry();
          if (t && t.length) slice.audioPlayback = t;
        } catch (e) { /* telemetry must never cost a submission */ }
      }

      function buildSaveFn(el) {
        var name = el.getAttribute('data-pd-save');                            // source NAME = default status key + payload path
        var s = sources[name];
        var writeKey = (s && s.writeKey) || el.getAttribute('data-pd-write-target') || name; // resolve the capability writeKey
        var base = el.getAttribute('data-pd-from') || name;                    // which model path to write FROM (default data.<name>)
        var fieldStr = el.getAttribute('data-pd-fields') || el.getAttribute('data-pd-write-fields') || '';
        var fields = fieldStr.split(',').map(function (f) { return f.trim(); }).filter(Boolean);
        var mods = modsOf(el, 'data-pd-save');
        return applyMods(el, function (e) {
          // The submit control is usually type="submit" inside a <form> carrying native
          // constraints (required / type=email / pattern / min…). Our preventDefault()
          // below cancels the browser's OWN constraint validation, so run it ourselves
          // FIRST: if the form is invalid, reportValidity() shows the native prompts and
          // we BAIL without posting — otherwise an incomplete submission just 400s
          // server-side ("missing required field"). No form / no reportValidity → post as
          // before (e.g. a game's imperative save button outside any form).
          e.preventDefault();
          var form = el.form || (el.closest && el.closest('form'));
          if (form && typeof form.reportValidity === 'function' && !form.reportValidity()) return;
          // v2 payload: a field subset → { f: data.<base>.f }, else the WHOLE object at data.<base>.
          var slice;
          if (fields.length) { slice = {}; fields.forEach(function (f) { slice[f] = getPath(model, base + '.' + f); }); }
          else { slice = getPath(model, base); }
          el.__pdSaveFired = true;   // the observer below stops defending a control once it has fired
          // A RECORDING is not a value — it is megabytes of audio that must be stored
          // before the row referencing it exists. Upload any takes first and substitute
          // their handles into the payload; a failed upload leaves that field empty and
          // the rest of the answers still submit, because losing a whole attempt over one
          // clip is the worse outcome.
          uploadRecordings(writeKey, slice).then(function (withClips) {
            attachAudioTelemetry(withClips);
            save(writeKey, snapshot(withClips), name); // snapshot detaches from reactivity before the durable write
          });
        }, mods);
      }
      listen(root, root, 'click', function (e) {
        var t = e && e.target;
        var el = t && t.closest ? t.closest('[data-pd-save]') : null;
        if (!el || (root.contains && !root.contains(el))) return;
        if (!el.__pdSaveFn) el.__pdSaveFn = buildSaveFn(el);
        el.__pdSaveFn(e);
      }, true);

      // DISABLED-STATE DEFENCE — the one hole delegation cannot close (owner 2026-08-05).
      //
      // A disabled control dispatches NO click, so no listener of ours can run. A real generated
      // quiz locked its current question with
      //   document.querySelectorAll('input,button').forEach(x => x.disabled = true)
      // which also disabled the SUBMIT button in a section it never re-rendered — the attempt
      // could then never be sent, with no click, no error and nothing to see.
      //
      // So: a control that declares data-pd-save has handed its click to the runtime, and the
      // runtime defends it — but ONLY until it has actually saved. Disabling a submit button AFTER
      // it fires is the correct anti-double-submit pattern and is left alone; disabling one that
      // has never fired is the bug above, and is reverted with a warning naming the control.
      if (global.MutationObserver && root.querySelectorAll) {
        var saveGuard = new global.MutationObserver(function (recs) {
          for (var i = 0; i < recs.length; i++) {
            var el = recs[i].target;
            if (!el || !el.hasAttribute || !el.hasAttribute('data-pd-save')) continue;
            if (!el.disabled || el.__pdSaveFired) continue;   // not disabled, or it already did its job
            el.disabled = false;
            if (!el.__pdSaveGuardWarned) {
              el.__pdSaveGuardWarned = true;
              warn('data-pd-save="' + el.getAttribute('data-pd-save') + '" was disabled before it saved — re-enabled. '
                 + 'Scope your disable to the container you are rendering, not the whole document.');
            }
          }
        });
        saveGuard.observe(root, { attributes: true, subtree: true, attributeFilter: ['disabled'] });
        addDisposer(root, function () { saveGuard.disconnect(); });
      }
      each(root, '[data-pd-query]', function (el) { // action verb: RE-READ a source → refill data.<name>
        var name = el.getAttribute('data-pd-query'); if (!name) { warn('data-pd-query with empty source name on <' + el.tagName.toLowerCase() + '>'); return; }
        var mods = modsOf(el, 'data-pd-query');
        listen(el, el, 'click', applyMods(el, function (e) { if (el.form && e && e.preventDefault) e.preventDefault(); fetchSource(name); }, mods));
      });
      each(root, '[data-pd-set]', function (el) { // action verb: assign a closed literal to a model path
        var spec = el.getAttribute('data-pd-set'); var i = spec == null ? -1 : spec.indexOf(':');
        if (i < 0) { warn('data-pd-set needs "path:value" — got "' + spec + '"'); return; }
        var path = spec.slice(0, i).trim(), raw = spec.slice(i + 1).trim();
        if (!path) { warn('data-pd-set with empty path'); return; }
        listen(el, el, 'click', function (e) { if (el.form && e && e.preventDefault) e.preventDefault(); setPath(model, path, coerceLiteral(raw)); });
      });
      each(root, '[data-pd-toggle]', function (el) { // action verb: boolean flip of a model path
        var path = el.getAttribute('data-pd-toggle'); if (!path) { warn('data-pd-toggle with empty path on <' + el.tagName.toLowerCase() + '>'); return; }
        listen(el, el, 'click', function (e) { if (el.form && e && e.preventDefault) e.preventDefault(); setPath(model, path, !getPath(model, path)); });
      });
      each(root, '[data-pd-cloak]', function (el) { el.removeAttribute('data-pd-cloak'); }); // shown once hydrated
    }

    // ── durable write → DELEGATED to PDDataClient.save (durable in-memory queue +
    //    localStorage mirror, idempotency, at-least-once with idempotent reconcile,
    //    park-on-exhaustion, sandbox in editor/preview). The runtime just tracks a
    //    per-write status in the reactive model so markers can bind to it. A write is
    //    by a capability WRITEKEY (symmetric to a read's readKey) — POST /ds/:writeKey. ──
    function fallbackSave(writeKey, data) { // only when no PDDataClient is present
      return apiFetch('/api/v1/ds/' + encodeURIComponent(writeKey), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ data: data }) })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return { ok: true }; });
    }
    // Set the per-write status in the reactive model. Alongside the STRING
    // `_status.<name>` ('saving'|'saved'|'error') we ALSO expose three BOOLEAN
    // siblings — `_status.<name>Saving` / `<name>Saved` / `<name>Error` — because
    // data-pd-show / data-pd-if take a truthy MODEL PATH only (no expressions): a
    // page CANNOT gate a success confirmation on `_status.<name> === 'saved'` (that
    // whole string is read as one literal path → resolves to undefined → hidden
    // forever). The boolean flag IS a truthy path, so `data-pd-show="_status.<name>Saved"`
    // shows the confirmation ONLY on success; `<name>Error` gates the failure block
    // and `<name>Message` carries its reason.
    function setStatus(statusKey, state, message) {
      setPath(model, '_status.' + statusKey, state);
      setPath(model, '_status.' + statusKey + 'Saving', state === 'saving');
      setPath(model, '_status.' + statusKey + 'Saved', state === 'saved');
      setPath(model, '_status.' + statusKey + 'Error', state === 'error');
      setPath(model, '_status.' + statusKey + 'Message', message || '');
    }
    // save(nameOrWriteKey, data[, statusKey]): accept a SOURCE NAME (resolved to its
    // writeKey) OR a raw writeKey. statusKey defaults to whatever the caller passed
    // (the name, so a marker can bind _status.<name>...).
    function save(nameOrWriteKey, data, statusKey) { // Promise; resolves on durable ack, rejects on terminal failure
      var s = sources[nameOrWriteKey];
      // A DECLARED source resolves ONLY to its own key. The `|| nameOrWriteKey` fallback is what
      // makes a raw-key save work, but applying it to a declared-but-keyless source hands the
      // ALIAS to the server as if it were a capability key: the page POSTs /ds/<alias> and gets
      // back 400 "Invalid key format" — a confusing server rejection standing in for a local
      // configuration error the page could have reported itself (live incident 2026-09-17, a
      // published quiz booted with `quizResults: { writeKey: "" }`). Fail here, say why, and let
      // _status.<alias>Error surface it.
      var writeKey = s ? s.writeKey : nameOrWriteKey;
      statusKey = statusKey || nameOrWriteKey;
      if (!writeKey) {
        var why = s
          ? 'save needs a writeKey — source "' + nameOrWriteKey + '" was declared without one (provision it and put the minted key in pdDataSources)'
          : 'save needs a writeKey';
        setStatus(statusKey, 'error', why); // the page's _status.<alias>Error / …Message say so
        return Promise.reject(new Error(why));
      }
      if (DBG) dlog('save() → writeKey', String(writeKey).slice(0, 8), '| via', (s ? 'source:' + nameOrWriteKey : 'raw-writeKey'), '| data keys=', (data && typeof data === 'object' ? Object.keys(data).length : typeof data), '| assessmentId=', (data && typeof data === 'object' && 'assessmentId' in data ? data.assessmentId : '(MISSING)'), '| schemaContract=', !!(data && typeof data === 'object' && 'schemaContract' in data), '| keys:', (data && typeof data === 'object' ? Object.keys(data).join(',') : '-'));
      setStatus(statusKey, 'saving', ''); // clears any prior reason + flags
      var p = dc ? dc.save({ writeKey: writeKey }, data) : fallbackSave(writeKey, data);
      return p.then(
        function (r) { setStatus(statusKey, 'saved', ''); draftClear(s ? nameOrWriteKey : null); if (global.document) $dispatch(document.body, 'pd:saved', { writeKey: writeKey }); refetchAfterSave(); return r; },
        // On failure, the reason lives at `_status.<name>Message` (the server's
        // {error:"…"} — e.g. "missing required field: email") so a marker shows WHY,
        // not just "error". Bind it with data-pd-text="_status.<name>Message".
        function (e) { setStatus(statusKey, 'error', (e && e.message) || 'Something went wrong. Please try again.'); throw e; }
      );
    }
    function status(key) { return getPath(model, '_status.' + key); }
    // Imperative escape hatch — read by SOURCE NAME (uses its readKey) or a RAW readKey.
    // Returns the ADAPTED rows (external → the value); does NOT auto-assign — the caller
    // assigns (e.g. `app.data.sales = await app.read('sales')`). Optional `params` override.
    function readImperative(nameOrKey, params) {
      var s = sources[nameOrKey], desc;
      if (s && s.kind === 'external') desc = { kind: 'external', url: s.url, transport: s.transport, params: params || s.params };
      else if (s) desc = { kind: 'dataset', readKey: s.readKey, params: params || model.params };
      else desc = { kind: 'dataset', readKey: nameOrKey, params: params || model.params };
      var read = dc ? dc.read(desc) : fallbackRead(desc);
      return read.then(function (j) { return (s && s.kind === 'external') ? ((j && j.data != null) ? j.data : j) : adaptServerRows(j); });
    }

    function $dispatch(el, name, detail) { el.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true })); }
    function cleanup(node) { if (node.__pdManaged) return; var els = [node].concat(node.querySelectorAll ? Array.from(node.querySelectorAll('*')) : []); els.forEach(function (e) { var a = disposers.get(e); if (a) { a.forEach(function (f) { try { f(); } catch (_) {} }); disposers.delete(e); } }); }

    // ── lifecycle: auto-hydrate added nodes, cleanup removed ones ──
    var mo = null;
    function start() {
      if (!global.document || !document.body) return;
      injectRuntimeStyles(global.document); // zero-specificity KPI-number default (author CSS wins)
      if (global.MutationObserver && !mo) {
        mo = new MutationObserver(function (muts) {
          try { muts.forEach(function (m) { m.addedNodes.forEach(function (n) { if (n.nodeType === 1) { scan(n); checkUnknown(n); } }); m.removedNodes.forEach(function (n) { if (n.nodeType === 1) cleanup(n); }); }); }
          catch (e) { logErr('observer', e); }
        });
        mo.observe(document.body, { childList: true, subtree: true });
      }
      scan(document.body); checkUnknown(document.body);
      prefillRespondentName();
    }
    // ── CLASS-LINK NAME (spec-return-loops-course-and-assign.md §3.4) ──
    // A student who tapped their name on the class link has already said who they are; the data
    // client keeps that roster name for this tab (`PDDataClient.respondentName()`). Fill an EMPTY
    // `<alias>.userFullName` (the name field of the assessment / response contracts) with it, after
    // the scan so a field bound only in markup exists too, so the page does not ask again. Never
    // over anything already there: a name the author declared or the student typed wins.
    function prefillRespondentName() {
      var n = '';
      try { n = (global.PDDataClient && typeof global.PDDataClient.respondentName === 'function' && global.PDDataClient.respondentName()) || ''; } catch (e) { n = ''; }
      if (!n || typeof n !== 'string') return;
      for (var k in model) {
        if (!Object.prototype.hasOwnProperty.call(model, k) || unsafeKey(k) || k === '_status' || k === 'params') continue;
        var o = model[k];
        if (!o || typeof o !== 'object' || Array.isArray(o) || !Object.prototype.hasOwnProperty.call(o, 'userFullName')) continue;
        if (o.userFullName === '' || o.userFullName == null) o.userFullName = n;
      }
    }
    // Full teardown — disconnect the observer, stop every poll + write-retry,
    // remove the global listeners, run every disposer. For SPA hosts / tests that
    // mount + unmount the runtime (zero leaks, no orphaned timers/listeners).
    function destroy() {
      try { if (mo) { mo.disconnect(); mo = null; } } catch (e) {}
      intervals.forEach(function (t) { try { clearInterval(t); } catch (e) {} }); intervals.length = 0;
      if (refetchTimer) { clearTimeout(refetchTimer); refetchTimer = null; } // no re-read after teardown
      try { if (global.document) document.removeEventListener('visibilitychange', onVis); } catch (e) {}
      try { if (global.removeEventListener) global.removeEventListener('online', onOnline); } catch (e) {}
      try { if (dc && dc.destroy) dc.destroy(); } catch (e) {} // tear down the data client (queue/retries/AbortControllers)
      try { if (draftRunner) stop(draftRunner); } catch (e) {} // stop the draft-persist effect
      try { cancelDraftPersist(); } catch (e) {} // clear any pending draft write
      try { if (global.removeEventListener) global.removeEventListener('pagehide', onDraftPageHide); } catch (e) {}
      if (global.document) cleanup(document.body);
    }

    // Declare + fetch the sources passed at boot (JS config, not DOM). The v2 boot key
    // is `pdDataSources` (unmistakably distinct from the free `data` model); `sources`
    // is accepted as a fallback. Each READ source kicks off a fetch that fills
    // data.<name>; the markers below render reactively as the data lands. A write-only
    // source registers but does not fetch.
    var __srcCfg = opts.pdDataSources || opts.sources;
    if (__srcCfg) { for (var __a in __srcCfg) if (Object.prototype.hasOwnProperty.call(__srcCfg, __a)) addSource(__a, __srcCfg[__a]); }

    // ── draft persistence wiring (see the block near model creation) ──
    // localStorage access can throw (privacy mode / disabled storage) — guard every touch.
    function draftStore() { try { return global.localStorage || null; } catch (e) { return null; } }
    // Stored draft = { t:<lastEditMs>, v:<inputs> }. The timestamp powers TTL expiry + LRU
    // eviction so localStorage never fills with abandoned forms (a draft that is never submitted
    // would otherwise live forever). (A legacy raw-inputs object with no `t` is still readable.)
    function draftKeysIn(ls) { var out = []; try { for (var i = 0; i < ls.length; i++) { var k = ls.key(i); if (k && k.indexOf(DRAFT_PREFIX) === 0) out.push(k); } } catch (e) {} return out; }
    function draftTs(ls, k) { try { var o = JSON.parse(ls.getItem(k)); return (o && typeof o.t === 'number') ? o.t : 0; } catch (e) { return 0; } }
    // Reclaim space on this ORIGIN: drop every EXPIRED draft (any form, TTL past) and, if still
    // over the cap, the OLDEST — so abandoned drafts get cleaned up on the next form visit. Bounded + cheap.
    function draftSweep(ls) {
      try {
        var now = Date.now(), live = [];
        draftKeysIn(ls).forEach(function (k) { var t = draftTs(ls, k); if (t && now - t > DRAFT_TTL_MS) { try { ls.removeItem(k); } catch (e) {} } else live.push({ k: k, t: t }); });
        if (live.length > DRAFT_MAX) { live.sort(function (a, b) { return a.t - b.t; }); for (var i = 0; i < live.length - DRAFT_MAX; i++) { try { ls.removeItem(live[i].k); } catch (e) {} } }
      } catch (e) {}
    }
    // A key is RESERVED (never drafted) if it's `_status`/`params`, or a READ source —
    // one whose `data.<name>` is SERVER-FETCHED (has a readKey or an external url), re-fetched
    // fresh on every load, so it's never a draft. A WRITE-ONLY source's `data.<name>` is the
    // VISITOR'S INPUT (the capture slice a form collects) — it MUST be drafted; that IS cross-page
    // persistence. (Bug fix: the old check reserved EVERY source, so a write-only form's own
    // entries were excluded from the draft → a multi-page wizard never carried data across pages.)
    function reservedKey(k) {
      if (k === '_status' || k === 'params') return true;
      var s = sources[k];
      return !!s && (!!s.readKey || !!s.url); // read/external → server data → not a draft; write-only → drafted
    }
    // Snapshot the FREE model (author data only) for the draft mirror.
    function freeModelSnapshot() {
      var out = {};
      for (var k in model) {
        if (!Object.prototype.hasOwnProperty.call(model, k)) continue;
        if (reservedKey(k) || unsafeKey(k)) continue;
        out[k] = model[k];
      }
      return out;
    }
    // The draft TARGETS: per-source (a source flagged `draft: true` → its `data.<alias>` persists,
    // keyed by the ALIAS — its stable identity) PLUS the legacy top-level `draftKey` (the WHOLE
    // free model under one key). A page opts persistence in PER-SOURCE now (`{ writeKey, draft:true }`);
    // the top-level `draftKey` stays supported for back-compat. Multiple write sources each persist
    // independently under their own alias.
    function draftTargets() {
      // localStorage DRAFT DISABLED for now (owner 2026-07-25) — paired with pd-data-client's mirror
      // being off. In-progress capture goes DIRECTLY to the server on save; there is NO localStorage
      // draft of ANY kind (per-source `draft:true` OR the legacy top-level `draftKey`), so the
      // cross-page pollution / partial-boot / stale-draft / leaked-submit-state class simply cannot
      // occur. Multi-page in-progress carry returns via the server-revision architecture (a page's
      // saves append/upsert revisions server-side; reload restores the latest). Return [] to make
      // draftLoad + draftPersist no-ops without touching localStorage.
      return [];
      // eslint-disable-next-line no-unreachable
      var out = [];
      for (var a in sources) if (Object.prototype.hasOwnProperty.call(sources, a) && sources[a].draft && !unsafeKey(a)) out.push({ key: a, alias: a });
      if (draftKey) out.push({ key: draftKey, alias: null }); // legacy: whole free model under one key
      return out;
    }
    function draftLoad() { // restore saved slices BEFORE the first scan so data-pd-model shows them
      var tg = draftTargets(); if (!tg.length) return;
      var ls = draftStore(); if (!ls) return;
      draftSweep(ls); // reclaim expired/over-cap drafts on this origin on every form visit
      tg.forEach(function (t) {
        var raw; try { raw = ls.getItem(DRAFT_PREFIX + t.key); } catch (e) { return; }
        if (!raw) return;
        var parsed; try { parsed = JSON.parse(raw); } catch (e) { return; }
        var saved, ts = 0;
        if (parsed && typeof parsed.t === 'number' && parsed.v !== undefined) { saved = parsed.v; ts = parsed.t; }
        else if (parsed && typeof parsed === 'object') { saved = parsed; } // legacy raw format
        else return;
        if (ts && Date.now() - ts > DRAFT_TTL_MS) { try { ls.removeItem(DRAFT_PREFIX + t.key); } catch (e) {} return; } // expired → discard
        if (t.alias == null) { // legacy top-level: saved = { key: slice } over the whole free model
          if (saved && typeof saved === 'object') for (var k in saved) if (Object.prototype.hasOwnProperty.call(saved, k) && !unsafeKey(k) && !reservedKey(k)) { model[k] = saved[k]; rehydratedPaths[k] = 1; }
        } else { // per-source: saved = the source's own slice (data.<alias>). FAITHFUL carry —
          // load WHATEVER the draft holds into memory, verbatim. This is the whole point: a multi-page
          // form ACCUMULATES here (each page loads what's there, the visitor adds more, save persists
          // it back), so the draft is the running snapshot. Do NOT merge/overlay/shape-guard it — a
          // draft is only ever PARTIAL when something UPSTREAM emptied the model before persist (a
          // producer bug — e.g. a stray second boot()); fix that, never second-guess the draft here.
          if (DBG) dlog('draftLoad RESTORE alias', t.alias, '| boot-slice keys=', (model[t.alias] ? Object.keys(model[t.alias]).length : 0), '| draft keys=', (saved && typeof saved === 'object' ? Object.keys(saved).length : 0), '| draft.assessmentId=', (saved && typeof saved === 'object' && 'assessmentId' in saved ? saved.assessmentId : '(MISSING)'));
          model[t.alias] = saved; rehydratedPaths[t.alias] = 1;
        }
      });
    }
    // The runtime is a neutral PERSISTENCE MECHANISM, not a form-lifecycle policy: it mirrors
    // whatever is in the free model and takes any subsequent change — it never permanently stops
    // persisting. clearDraft() removes the stored draft AND cancels any pending debounced write,
    // so a clear STICKS (no late write resurrects a just-cleared draft) — but persistence RESUMES
    // the instant inputs change again, so a page that accepts a NEW entry after submit (e.g.
    // "submit another") just keeps binding inputs and it re-persists automatically. Whether to
    // stop, reset, or keep collecting after a save is the PAGE's decision, not the runtime's.
    function draftPersist() {
      var tg = draftTargets(); if (!tg.length) return;
      var ls = draftStore(); if (!ls) return;
      tg.forEach(function (t) {
        var v = t.alias == null ? freeModelSnapshot() : (model[t.alias] != null ? model[t.alias] : {});
        var json; try { json = JSON.stringify({ t: Date.now(), v: v }); } catch (e) { return; }
        if (json.length > DRAFT_MAX_BYTES) return; // one draft must not eat the origin's quota
        try { ls.setItem(DRAFT_PREFIX + t.key, json); }
        catch (e) { try { draftSweep(ls); ls.setItem(DRAFT_PREFIX + t.key, json); } catch (e2) {} } // quota → reclaim + retry once
      });
    }
    function scheduleDraftPersist() { if (draftTimer) clearTimeout(draftTimer); draftTimer = setTimeout(function () { draftTimer = null; draftPersist(); }, 400); }
    function cancelDraftPersist() { if (draftTimer) { clearTimeout(draftTimer); draftTimer = null; } }
    function draftClear(which) { // on a source's save: clear its per-source draft + the legacy whole-model draft its data lives in; no arg → clear ALL targets
      cancelDraftPersist(); var ls = draftStore(); if (!ls) return;
      draftTargets().forEach(function (t) { if (which == null || t.alias == null || t.key === which || t.alias === which) { try { ls.removeItem(DRAFT_PREFIX + t.key); } catch (e) {} } });
    }
    // Flush the LATEST inputs synchronously before the page unloads (hyperlink nav to the next
    // step) — cancel the pending debounce and write NOW so the last keystroke before "Next" is never lost.
    function onDraftPageHide() { cancelDraftPersist(); draftPersist(); }
    function setupDraftPersist() { // deep-track each draft slice → debounced mirror to localStorage
      if (!draftTargets().length) return;
      draftRunner = rawEffect(function () { try { JSON.stringify(draftTargets().map(function (t) { return t.alias == null ? freeModelSnapshot() : model[t.alias]; })); } catch (e) {} scheduleDraftPersist(); }, { scheduler: function () { enqueue(draftRunner); } });
      if (global.addEventListener) global.addEventListener('pagehide', onDraftPageHide);
    }
    draftLoad(); setupDraftPersist();

    // Wire the DOM once it is FULLY PARSED — defer to DOMContentLoaded whenever the parser
    // is still running (readyState 'loading'). This is the Alpine/petite-vue model: `boot()`
    // may sit ANYWHERE — in <head>, at the TOP of <body>, or at the end — and every marker is
    // wired, because markers BELOW the boot() are parsed before the one whole-tree scan runs.
    // A late / dynamic / SPA boot (DOM already parsed) scans NOW; the MutationObserver set up
    // in start() then keeps any later dynamic insertions live. (Gating on `document.body`
    // instead made a top-of-<body> boot scan a half-parsed tree — placement was load-bearing.)
    if (!global.document) { /* no DOM — API-only (SSR/test) */ }
    else if (document.readyState !== 'loading') start();
    else document.addEventListener('DOMContentLoaded', start, { once: true });
    var app = {
      version: VERSION, model: model, data: model, sources: sources, dataClient: dc, maxRows: MAX_ROWS, // `data` = the live reactive model root (v2)
      addSource: addSource, refetch: refetch, removeSource: removeSource, read: readImperative, // imperative source lifecycle + read escape hatch
      save: save, status: status, clearDraft: draftClear, draftKey: draftKey, // durable write + status + multi-page draft
      scan: scan, cleanup: cleanup, destroy: destroy, computed: computed, $watch: watch,
      $dispatch: function (n, d) { if (global.document) $dispatch(document.body, n, d); },
      $nextTick: function (fn) { (global.queueMicrotask || function (f) { Promise.resolve().then(f); })(fn); },
      // TEMP DEBUG: a plain-object snapshot of the live model (call `PDReactiveRuntime.app.dump()`).
      dump: function () { try { return JSON.parse(JSON.stringify(model)); } catch (e) { return null; } }
    };
    // Expose the booted app on the GLOBAL so imperative author code can reach it WITHOUT
    // capturing boot()'s return value. A very common author pattern is `boot({...})` (return
    // discarded) then, in a custom submit handler, `PDReactiveRuntime.app.save(alias, data)`.
    // Without a handle the author hand-rolls `fetch('/api/v1/ds/<writeKey>', {body: <flat>})`
    // — which 400s ("data object required") because it omits the {data} envelope save() adds.
    // `_app` is an accepted alias (a frequent guess). Multiple boots → the FIRST wins and later
    // calls merge into it (see BOOTED above). save(alias,…) resolves the alias→writeKey + wraps {data}.
    try { if (global.PDReactiveRuntime) { global.PDReactiveRuntime.app = app; global.PDReactiveRuntime._app = app; } } catch (_e) {}
    BOOTED = app;
    return app;
  }

  global.PDReactiveRuntime = { version: VERSION, boot: boot };
})(typeof window !== 'undefined' ? window : globalThis);
